import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getLocaleFromPathname } from './routing';

// Minimal starter dictionaries. Add more keys as you translate more UI.
const resources = {
  es: {
    translation: {
      common: {
        toggleMenu: 'Abrir/cerrar menú',
        switchToEn: 'Cambiar a inglés',
        switchToEs: 'Cambiar a español',
        switchToPt: 'Cambiar a portugués',
        backHomeAria: 'Volver al inicio',
        confirm: 'Confirmar',
        cancel: 'Cancelar',
        playNow: 'Jugar ahora',
        toggleTheme: 'Cambiar modo claro/oscuro',
        themeDark: 'Oscuro',
        themeLight: 'Claro',
        close: 'Cerrar',
        understood: 'Entendido',
        information: 'Información',
        leave: 'Salir',
        stay: 'Quedarme',
        loading: 'Cargando...',
        menu: 'Menú'
      },
      cookieConsent: {
        textPrefix:
          'Utilizamos cookies propias y de terceros para mejorar tu experiencia, analizar el tráfico y mostrar publicidad personalizada. Al continuar navegando, aceptas nuestra',
        privacyPolicyLinkText: 'Política de Privacidad',
        accept: 'Aceptar'
      },
      nav: {
        home: 'Inicio',
        blog: 'Blog',
        contact: 'Contacto',
        play: 'Jugar'
      },
      siteFooter: {
        brand: 'El Impostor Game',
        description: 'Descubre al impostor en este emocionante juego social online.',
        linksTitle: 'Enlaces',
        links: {
          blog: 'Blog',
          about: 'Acerca de',
          contact: 'Contacto'
        },
        legalTitle: 'Legal',
        legal: {
          privacy: 'Privacidad',
          terms: 'Términos'
        },
        followTitle: 'Síguenos',
        followText: 'Mantente al día con las últimas novedades y actualizaciones del juego.',
        rightsReserved: 'Todos los derechos reservados.',
        madeWithLove: 'Desarrollado con ❤️ por RurayLabs'
      },
      home: {
        pageTitle: 'El Impostor Game - Juego Social Online',
        seo: {
          title: 'El Impostor Game - Juego Social Online',
          description:
            'El Impostor ▷ Juego online GRATIS de deducción social. Descubre quién es el impostor jugando con amigos. Multijugador en tiempo real y modo mesa presencial. ¡Juega ahora!',
          keywords:
            'el impostor, el impostor juego, juego el impostor, impostor online, el impostor online, juego impostor gratis, el impostor game'
        },
        brand: 'El Impostor Game',
        moreInfoTitle: '¿Cómo funciona?',
        hero: {
          tagline: '¡Encuentra al impostor antes de que sea demasiado tarde!'
        },
        online: {
          title: '🌐 Multijugador Online',
          moreInfoAria: 'Más información sobre modo online',
          description: 'Conecta con amigos desde cualquier lugar en tiempo real',
          createRoom: '🏠 Crear Sala',
          joinRoom: '🚪 Unirse a Sala',
          modal: {
            title: 'Multijugador Online',
            description: 'Conecta con amigos desde cualquier lugar del mundo en tiempo real',
            steps: [
              'Crea una sala nueva o únete con un código de 6 caracteres',
              'Espera a que todos los jugadores se conecten desde sus dispositivos',
              'El sistema asigna roles automáticamente (jugadores e impostores)',
              'En cada ronda, escribe mensajes y debate con los demás',
              'Vota para expulsar a quien creas que es el impostor',
              '¡Los jugadores ganan si eliminan al impostor!'
            ]
          }
        },
        divider: {
          or: 'O'
        },
        table: {
          title: '🎲 Modo Mesa',
          moreInfoAria: 'Más información sobre modo mesa',
          description: 'Un solo dispositivo, máxima interacción cara a cara',
          play: '🎲 Jugar en Mesa',
          modal: {
            title: 'Modo Mesa',
            description: 'Disfruta de la interacción cara a cara sin distracciones digitales',
            steps: [
              'Reúne a todos los jugadores en un mismo lugar',
              'Ingresa los nombres en un solo dispositivo compartido',
              'Cada jugador ve su rol de forma privada (¡no miren!)',
              'Debatan cara a cara sobre quién es el impostor',
              'Voten levantando la mano o usando el dispositivo',
              '¡Disfruta de la interacción presencial y descubre al impostor!'
            ]
          }
        },
        links: {
          contact: 'Contacto',
          about: 'Acerca de',
          guide: 'Guía y Tutorial'
        },
        recommended: {
          pages: 'Páginas Recomendadas',
          games: 'Juegos Recomendados',
          desc: {
            brainTwins: 'El juego donde las mentes se sincronizan',
            imgParser: 'Transforma tus imágenes en segundos',
            dailyQuiz: '¿Cuánto sabes? ¡Pon a prueba tu mente!'
          }
        },
        footer: {
          finePrint: 'Mejor con 3-10 jugadores • Tiempo real • Multisala',
          recommended: 'Juegos recomendados:'
        }
      },
      create: {
        pageTitle: 'Crear Sala - El Impostor Game',
        seo: {
          title: 'Crear Sala - El Impostor Game',
          description:
            'Crea una sala privada para jugar a El Impostor con tus amigos en tiempo real. Configura el número de jugadores e impostores.'
        },
        backHome: '← Volver al inicio',
        header: {
          title: '🏠 Crear Nueva Sala',
          subtitle: 'Configura tu sala y invita a tus amigos'
        },
        errors: {
          createFailed: 'Error al crear la sala. Inténtalo de nuevo.',
          nameRequiredToJoinPublic: 'Ingresa tu nombre arriba para poder unirte a una sala pública.'
        },
        form: {
          nameLabel: 'Tu nombre',
          namePlaceholder: 'Ingresa tu nombre',
          passwordToggleLabel: 'Proteger con contraseña',
          passwordToggleHelp: 'Solo podrán unirse quienes la conozcan',
          passwordLabel: 'Contraseña de la sala',
          passwordPlaceholder: 'Ingresa una contraseña',
          minPlayersLabel: 'Mín. jugadores',
          maxPlayersLabel: 'Máx. jugadores',
          characterLabel: 'Elige un personaje'
        },
        activeRooms: {
          title: 'Tus salas activas:',
          goToRoom: 'Ir a sala',
          hint: '💡 Puedes entrar a estas salas y cerrarlas para liberar espacio'
        },
        submit: {
          creating: 'Creando sala...',
          create: '🚀 Crear Sala'
        },
        footer: {
          autoCloseNote: 'La sala se cerrará automáticamente después de 2 horas de inactividad'
        }
      },
      join: {
        pageTitle: 'Unirse a Sala - El Impostor Game',
        seo: {
          title: 'Unirse a Sala - El Impostor Game',
          description: 'Únete a una sala de El Impostor con un código de 6 caracteres y juega con tus amigos en tiempo real.'
        },
        backHome: '← Volver al inicio',
        header: {
          title: '🚪 Unirse a Sala',
          subtitle: 'Ingresa el código para unirte al juego'
        },
        errors: {
          roomNotFoundWithHint: 'Sala no encontrada. Verifica el código.',
          roomNotFound: 'Sala no encontrada.',
          checkRoomFailed: 'Error al verificar la sala.',
          wrongPassword: 'Contraseña incorrecta.',
          joinFailed: 'Error al unirse a la sala. Inténtalo de nuevo.',
          publicRoomsFailed: 'No se pudieron cargar las salas públicas.'
        },
        form: {
          roomCodeLabel: 'Código de la sala',
          roomCodeHelp: 'Código de 6 caracteres proporcionado por el creador',
          nameLabel: 'Tu nombre',
          namePlaceholder: 'Ingresa tu nombre',
          characterLabel: 'Elige un personaje',
          passwordLabel: 'Contraseña de la sala',
          passwordPlaceholder: 'Ingresa la contraseña'
        },
        roomInfo: {
          found: '✅ Sala encontrada',
          createdBy: 'Creada por:',
          statusLabel: 'Estado:',
          statusWaiting: 'Esperando jugadores',
          statusPlaying: 'En juego',
          requiresPassword: '🔒 Requiere contraseña',
          playersCount: '{{current}}/{{max}} jugadores'
        },
        submit: {
          joining: 'Uniéndose...',
          playingInProgress: '🎮 Juego en progreso',
          joinRoom: '🚀 Unirse a la Sala'
        },
        noCode: {
          prefix: '¿No tienes un código? Pregunta al creador de la sala o',
          createNew: 'crea una nueva'
        },
        publicRooms: {
          title: '🌐 Salas públicas activas',
          refreshing: 'Actualizando...',
          refresh: '🔄 Actualizar',
          loading: 'Cargando salas públicas...',
          emptyTitle: 'No hay salas públicas disponibles ahora.',
          emptySubtitle: '¡Sé el primero en crear una!',
          hint: '💡 Las salas se actualizan automáticamente cada 30 segundos',
          newBadge: '✨ Nueva',
          hostFallback: 'Anfitrión',
          full: '🔒 Llena',
          joinFromList: '🚀 Unirme',
          timeAgo: {
            justCreated: 'Recién creada',
            oneMinute: 'Hace 1 min',
            minutes: 'Hace {{minutes}} min',
            hours: 'Hace {{hours}} h'
          }
        }
      },
      contactPage: {
        pageTitle: 'Contacto - El Impostor Game',
        seo: {
          title: 'Contacto - El Impostor Game',
          description: 'Contáctanos para consultas de negocio o envíanos tus sugerencias y recomendaciones para mejorar El Impostor.'
        },
        header: {
          title: '📧 Contacto',
          subtitle: '¿Tienes una consulta de negocio o una sugerencia? ¡Nos encantaría escucharte!'
        },
        tabs: {
          business: '💼 Negocios',
          recommendations: '💡 Recomendaciones'
        },
        messages: {
          success: '¡Mensaje enviado exitosamente! Te contactaremos pronto.',
          error: 'Hubo un error al enviar el mensaje. Por favor, inténtalo de nuevo.'
        },
        business: {
          title: 'Consultas de Negocios',
          description:
            '¿Tienes una propuesta comercial, quieres colaborar o necesitas más información sobre nuestros servicios? Completa el formulario y nos pondremos en contacto contigo.',
          emailLabel: 'Correo Electrónico *',
          emailPlaceholder: 'tu@email.com',
          nameLabel: 'Nombre Completo *',
          namePlaceholder: 'Tu nombre completo',
          subjectLabel: 'Asunto *',
          subjectPlaceholder: 'Tema de tu consulta',
          descriptionLabel: 'Descripción *',
          descriptionPlaceholder: 'Describe detalladamente tu consulta, propuesta o solicitud...',
          submitSending: '📤 Enviando...',
          submit: '📤 Enviar Consulta'
        },
        recommendations: {
          title: 'Recomendaciones y Sugerencias',
          infoTitle: 'Envío Anónimo',
          infoText:
            'Puedes enviar solicitudes, mejoras, reportar bugs o cualquier sugerencia de manera completamente anónima. No necesitas proporcionar tu información personal.',
          description:
            'Tu feedback es muy valioso para nosotros. Ayúdanos a mejorar el juego compartiendo tus ideas, reportando problemas o sugiriendo nuevas características.',
          textareaLabel: 'Tu Sugerencia o Recomendación *',
          textareaPlaceholder:
            'Comparte tu sugerencia, idea de mejora, reporte de bug, nueva característica, o cualquier comentario que tengas sobre el juego...',
          examples:
            'Ejemplos: "Sería genial agregar más personajes", "Encontré un bug cuando...", "El juego podría mejorar si..."',
          privacyTitle: '🔒 Privacidad',
          privacyText:
            'Esta sugerencia será enviada de forma anónima. No almacenamos ninguna información personal que pueda identificarte. Simplemente queremos escuchar tus ideas para hacer el juego mejor.',
          submitSending: '📨 Enviando...',
          submit: '📨 Enviar Sugerencia'
        },
        footer: {
          preferOtherPrefix: '¿Prefieres contactarnos por otro medio? Encuéntranos en nuestras redes sociales o envía un email directo a'
        }
      },
      notFound: {
        seo: {
          title: 'Página no encontrada - 404 | El Impostor',
          description: 'La página que buscas no existe. Vuelve al inicio para jugar El Impostor online.'
        },
        title: 'Página No Encontrada',
        description: 'Lo sentimos, la página que buscas no existe o ha sido movida.',
        backHome: 'Volver al Inicio',
        goBlog: 'Ir al Blog'
      },

      blogGuide: {
        brand: 'Impostor Game',
        pageTitle: 'Cómo jugar El Impostor Game | Guía y Tutorial Completo',
        metaDescription:
          'Aprende a jugar El Impostor Game: reglas, roles, rondas, votaciones y estrategias. Guía completa paso a paso para nuevos jugadores.',
        nav: {
          home: 'Inicio',
          createRoom: 'Crear sala',
          join: 'Unirse'
        },
        title: 'Cómo jugar a Impostor Game: Guía y tutorial completo',
        intro:
          'Bienvenido a la guía oficial de Impostor Game. Aquí aprenderás qué es el juego, cómo se juega paso a paso, y todas las opciones disponibles para que puedas sacarle el máximo partido, ya sea con amigos, en clase o en eventos.',
        whatIs: {
          heading: '¿Qué es Impostor Game?',
          paragraph:
            'Impostor Game es un juego social por rondas inspirado en dinámicas de deducción. En cada partida hay jugadores normales y uno o varios impostores. Los jugadores escriben mensajes, debaten, y al final de cada ronda votan para expulsar a quien crean que es el impostor.',
          bullets: ['Ideal para 3 a 10 jugadores', 'Partidas rápidas en tiempo real', 'Perfecto para romper el hielo, clases y streamings']
        },
        howToPlay: {
          heading: 'Cómo jugar (paso a paso)',
          steps: [
            'Crea una sala nueva o únete con un código compartido.',
            'Se asignan roles de forma automática: jugadores e impostores.',
            'En cada ronda, escribe tu mensaje y lee a los demás.',
            'Debate con el grupo y utiliza la lógica para descubrir inconsistencias.',
            'Vota para expulsar a un sospechoso al final de la ronda.',
            'Gana el equipo que cumpla su objetivo: jugadores si eliminan al impostor, impostores si pasan desapercibidos.'
          ],
          ctaCreate: 'Crear sala ahora',
          ctaJoin: 'Unirme a una sala'
        },
        roles: {
          heading: 'Roles del juego',
          player:
            'Jugador: tu objetivo es identificar comportamientos sospechosos, hacer preguntas inteligentes y votar estratégicamente.',
          impostor: 'Impostor: tu objetivo es mezclarte, sembrar dudas y evitar que te descubran hasta el final.'
        },
        mechanics: {
          heading: 'Mecánicas de juego',
          bullets: [
            'Rondas con límite de tiempo para escribir y debatir.',
            'Votaciones anónimas y conteo automático.',
            'Revelación del expulsado y avance a la siguiente ronda.',
            'Soporte multisala para jugar con varios grupos.'
          ]
        },
        options: {
          heading: 'Opciones y configuración',
          bullets: [
            'Número de jugadores recomendado: 3 a 10.',
            'Roles asignados automáticamente para equilibrar la partida.',
            'Chat por rondas con control de ritmo.',
            'Votación final por ronda con desempates automáticos.'
          ]
        },
        tips: {
          heading: 'Consejos para ganar',
          bullets: [
            'Observa el lenguaje y las contradicciones.',
            'Haz preguntas abiertas para forzar a otros a explicarse.',
            'Como impostor, mantén mensajes creíbles y evita exagerar.'
          ]
        },
        faq: {
          heading: 'Preguntas frecuentes',
          items: [
            { q: '¿Necesito cuenta?', a: 'No, solo un nombre para entrar a una sala.' },
            { q: '¿Funciona en móvil?', a: 'Sí, está optimizado para dispositivos móviles.' },
            { q: '¿Cuánto dura una partida?', a: 'Entre 5 y 15 minutos, según el número de jugadores.' }
          ]
        },
        ready: {
          title: '¿Listo para jugar?',
          subtitle: 'Crea una sala e invita a tus amigos en segundos.',
          create: 'Crear sala',
          join: 'Unirse a sala',
          backHome: 'Volver al inicio'
        },
        footer: 'Impostor Game • Tutorial y guía de juego'
      },

      blogOnline: {
        brand: 'Impostor Game',
        pageTitle: 'Modo Online Multijugador - El Impostor Game',
        metaDescription:
          'Juega al Impostor Game en modo online multijugador con amigos desde cualquier lugar. Conexión en tiempo real, votaciones instantáneas y diversión garantizada.',
        nav: {
          home: 'Inicio',
          blog: 'Blog',
          createRoom: 'Crear sala'
        },
        title: '🌐 Modo Online Multijugador',
        subtitle: 'Juega con amigos desde cualquier lugar del mundo en tiempo real',
        whatIs: {
          heading: '¿Qué es el modo online?',
          paragraph:
            'El modo online multijugador de Impostor Game te permite jugar con tus amigos sin importar dónde se encuentren. Solo necesitas compartir un código de sala y todos pueden unirse desde sus dispositivos móviles o computadoras.',
          benefitTitle: '✨ Principal beneficio:',
          benefitText:
            'Conecta con amigos, familiares o compañeros de trabajo desde cualquier ubicación. Ideal para reuniones virtuales, clases online o simplemente para divertirse a distancia.'
        },
        features: {
          heading: 'Características principales',
          items: [
            'Conexión en tiempo real: Todos los jugadores ven las actualizaciones al instante',
            'Multisala: Varias partidas pueden jugarse simultáneamente',
            'Sin instalación: Solo necesitas un navegador web',
            'Compatible con móviles: Juega desde cualquier dispositivo',
            'Chat por rondas: Debate estratégicamente en cada fase del juego',
            'Votaciones anónimas: Nadie sabe quién votó a quién hasta el final'
          ]
        },
        steps: {
          heading: 'Cómo jugar paso a paso',
          items: [
            'Crear o unirse a una sala: El anfitrión crea una sala y comparte el código',
            'Esperar jugadores: Los demás se unen usando el código de 6 caracteres',
            'Asignación de roles: El sistema asigna automáticamente roles (jugadores e impostores)',
            'Rondas de juego: En cada ronda, escribe mensajes y lee lo que escriben otros',
            'Debate: Usa la lógica para identificar comportamientos sospechosos',
            'Votación: Al final de cada ronda, vota para expulsar a un sospechoso',
            'Victoria: Los jugadores ganan eliminando al impostor, los impostores ganan pasando desapercibidos'
          ],
          ctaCreate: 'Crear sala ahora',
          ctaJoin: 'Unirme a una sala'
        },
        useCases: {
          heading: 'Casos de uso ideales',
          items: [
            { title: 'Reuniones virtuales', description: 'Perfecto para romper el hielo en videollamadas de trabajo o eventos online' },
            { title: 'Educación online', description: 'Herramienta dinámica para clases virtuales y desarrollo de pensamiento crítico' },
            { title: 'Gaming con amigos', description: 'Organiza partidas con amigos de diferentes ciudades o países' },
            { title: 'Streaming', description: 'Ideal para streamers que quieren interactuar con su comunidad' }
          ]
        },
        tips: {
          heading: 'Consejos para el modo online',
          items: [
            'Usa videollamada paralela: Combina el juego con Discord, Zoom o Meet para más inmersión',
            'Establece reglas claras: Define tiempo de debate y normas antes de empezar',
            'Mantén grupos equilibrados: Ideal entre 4-8 jugadores para mejores partidas',
            'Conexión estable: Asegúrate de tener buena conexión a internet',
            'Sé respetuoso: Mantén un ambiente amigable y divertido para todos'
          ]
        },
        faq: {
          heading: 'Preguntas frecuentes',
          items: [
            { q: '¿Necesito cuenta para jugar?', a: 'No, solo necesitas un nombre para entrar a la sala.' },
            { q: '¿Cuántos jugadores pueden unirse?', a: 'Recomendamos entre 3 y 10 jugadores para una mejor experiencia.' },
            { q: '¿Funciona en móvil?', a: 'Sí, está completamente optimizado para dispositivos móviles.' },
            { q: '¿Cuánto dura una partida?', a: 'Entre 5 y 15 minutos, dependiendo del número de jugadores y rondas.' },
            { q: '¿Puedo jugar en privado?', a: 'Sí, solo comparte el código con las personas que quieras invitar.' }
          ]
        },
        ready: {
          title: '¿Listo para jugar online?',
          subtitle: 'Crea una sala e invita a tus amigos en segundos. ¡La diversión está a un clic de distancia!',
          create: 'Crear sala',
          join: 'Unirse a sala',
          backHome: 'Volver al inicio'
        },
        footer: 'Impostor Game • Modo Online Multijugador'
      },

      blogTable: {
        brand: 'Impostor Game',
        pageTitle: 'Modo Mesa - El Impostor Game',
        metaDescription:
          'Juega al Impostor Game en modo mesa para reuniones presenciales. Un solo dispositivo, diversión cara a cara, sin necesidad de múltiples conexiones.',
        nav: {
          home: 'Inicio',
          blog: 'Blog',
          playTable: 'Jugar en Mesa'
        },
        title: '🎲 Modo Mesa',
        subtitle: 'Un solo dispositivo, máxima interacción cara a cara',
        whatIs: {
          heading: '¿Qué es el modo mesa?',
          paragraph:
            'El modo mesa de Impostor Game está diseñado para jugar en reuniones presenciales usando un solo dispositivo. Perfecto para fiestas, reuniones familiares, aulas o cualquier evento donde todos estén en el mismo lugar.',
          benefitTitle: '✨ Principal beneficio:',
          benefitText:
            'Disfruta de la interacción cara a cara sin distracciones digitales. Solo necesitas un dispositivo (tablet, laptop o pantalla) y todos los jugadores pueden participar viéndose directamente. Ideal para fortalecer vínculos sociales y crear momentos memorables.'
        },
        features: {
          heading: 'Características principales',
          items: [
            'Un solo dispositivo: No necesitas que cada jugador tenga su teléfono',
            'Revelación de roles individual: Cada jugador ve su rol de forma privada',
            'Discusión cara a cara: Debate en persona para mayor inmersión',
            'Votación presencial: Vota levantando la mano o usando el dispositivo',
            'Sin conexión a internet requerida: Una vez cargado, funciona offline',
            'Ideal para grupos locales: Perfecto para eventos presenciales'
          ]
        },
        steps: {
          heading: 'Cómo jugar paso a paso',
          items: [
            'Configurar la partida: Ingresa los nombres de todos los jugadores en un solo dispositivo',
            'Distribuir roles: El sistema asigna roles secretos a cada jugador',
            'Revelar roles: Cada jugador ve su rol de forma individual y privada',
            'Debate presencial: Discutan cara a cara sobre quién puede ser el impostor',
            'Votación: Cada jugador vota señalando o usando el dispositivo compartido',
            'Revelación: Se revela quién fue expulsado y su rol',
            'Victoria: El juego continúa hasta que se identifique al impostor o ganen los impostores'
          ],
          ctaPlay: 'Jugar en Modo Mesa'
        },
        useCases: {
          heading: 'Casos de uso ideales',
          items: [
            { title: 'Fiestas y reuniones', description: 'Perfecto para animar fiestas de cumpleaños, reuniones familiares o eventos sociales' },
            { title: 'Aulas y educación', description: 'Herramienta educativa para desarrollar habilidades de comunicación y pensamiento crítico' },
            { title: 'Team building', description: 'Actividad de integración para equipos de trabajo y dinámicas empresariales' },
            { title: 'Cafeterías y espacios sociales', description: 'Ideal para grupos de amigos que se reúnen en persona' }
          ]
        },
        advantages: {
          heading: 'Ventajas del modo mesa',
          items: [
            { title: 'Lenguaje corporal', description: 'Observa gestos, miradas y reacciones en tiempo real' },
            { title: 'Económico', description: 'No necesitas múltiples dispositivos o conexiones' },
            { title: 'Mayor inmersión', description: 'La interacción cara a cara hace el juego más emocionante' },
            { title: 'Configuración rápida', description: 'Comienza a jugar en menos de un minuto' }
          ]
        },
        tips: {
          heading: 'Consejos para el modo mesa',
          items: [
            'Usa una pantalla grande: Mejor experiencia con tablet, laptop o TV',
            'Privacidad en roles: Asegúrate de que cada jugador vea su rol sin que otros miren',
            'Establece límites de tiempo: Define cuánto dura cada ronda de debate',
            'Ambiente adecuado: Siéntense en círculo para que todos puedan verse',
            'Respeto y diversión: Mantén un ambiente amigable y divertido'
          ]
        },
        comparison: {
          heading: 'Mesa vs Online: ¿Cuál elegir?',
          headers: {
            aspect: 'Aspecto',
            table: 'Modo Mesa',
            online: 'Modo Online'
          },
          rows: [
            { aspect: 'Dispositivos necesarios', table: '1 solo dispositivo', online: '1 por jugador' },
            { aspect: 'Ubicación', table: 'Mismo lugar', online: 'Cualquier lugar' },
            { aspect: 'Interacción', table: 'Cara a cara', online: 'Virtual/texto' },
            { aspect: 'Ideal para', table: 'Fiestas, aulas, reuniones presenciales', online: 'Amigos a distancia, trabajo remoto' }
          ]
        },
        faq: {
          heading: 'Preguntas frecuentes',
          items: [
            { q: '¿Necesito internet para el modo mesa?', a: 'Solo para cargar la página inicialmente. Una vez cargado, funciona sin conexión.' },
            { q: '¿Cuántos jugadores pueden participar?', a: 'Recomendamos entre 3 y 10 jugadores para una mejor experiencia.' },
            { q: '¿Qué dispositivo es mejor?', a: 'Una tablet o laptop es ideal, aunque también funciona en teléfonos.' },
            { q: '¿Cómo se mantiene el secreto del rol?', a: 'El juego muestra el rol de forma individual y los demás jugadores no deben mirar.' },
            { q: '¿Puedo usar el modo mesa en el aula?', a: '¡Absolutamente! Es una excelente herramienta educativa para trabajar habilidades sociales y cognitivas.' }
          ]
        },
        ready: {
          title: '¿Listo para jugar en modo mesa?',
          subtitle: 'Reúne a tu grupo, prepara el dispositivo y ¡que comience la diversión cara a cara!',
          play: 'Jugar en Mesa',
          backHome: 'Volver al inicio'
        },
        footer: 'Impostor Game • Modo Mesa'
      },

      categorySelector: {
        selectPlaceholder: 'Selecciona una categoría',
        searchPlaceholder: '🔍 Buscar categoría...',
        noResults: 'No se encontraron categorías',
        charactersCount: '{{count}} personajes',
        showMore: 'Mostrar más ({{count}} restantes)',
        showing: 'Mostrando {{visible}} de {{total}} categorías',
        filteredSuffix: '(filtradas de {{all}} totales)'
      },

      gameRoom: {
        seo: {
          title: 'Sala {{roomCode}} - El Impostor Game',
          description:
            'Sala de juego de El Impostor en tiempo real. Participa en la deducción social y descubre quién es el impostor.'
        },
        header: {
          title: 'Sala {{roomCode}}',
          playersConnected: '{{count}} jugadores conectados',
          yourCharacterAlt: 'Tu personaje',
          roomArtAlt: 'Arte de la sala',
          copyLink: '📋 Copiar enlace',
          closeRoom: '🔒 Cerrar sala',
          leave: '🚪 Salir'
        },
        websocket: {
          connected: 'Conectado en tiempo real',
          disconnected: 'Desconectado - Reintentando...'
        },
        systemMessage: {
          default: 'Notificación del sistema'
        },
        reconnecting: {
          title: '🔄 Reconectando',
          restoring: 'Estamos restaurando tu sesión...',
          retrying: 'Reintentando conexión ({{current}}/{{max}})...',
          updatingSession: 'Actualizando sesión...',
          help: 'Si esto toma mucho tiempo, intenta recargar la página',
          brand: '🎭 El Impostor',
          overlayTitle: 'Reconectando a la partida',
          attempt: 'Intento {{current}} de {{max}}',
          server: 'Restableciendo conexión con el servidor...',
          overlayFooter: 'El Impostor - Juego Multijugador'
        },
        confirm: {
          beforeUnload: '¿Estás seguro que quieres salir? La partida está en curso.',
          leaveTitle: '¿Abandonar partida?',
          leaveMessage: '¿Estás seguro que quieres salir? La partida está en curso y perderás tu progreso.',
          closeTitle: '¿Cerrar sala?',
          closeMessage:
            '¿Estás seguro que quieres cerrar la sala? Todos los jugadores serán desconectados y la sala quedará inactiva. Esto liberará espacio para crear nuevas salas.'
        },
        errors: {
          loadRoomInfo: 'Error al cargar información de la sala',
          joinFailed: 'No se pudo unir a la sala',
          reconnectFailed: 'No se pudo reconectar',
          connectionError: '⚠️ Error de conexión: {{message}}. Intenta recargar la página manualmente.',
          startGame: 'Error al iniciar el juego',
          roomNotWaiting:
            'La sala no está en espera. Parece que hay una partida en curso. Si eres el creador, puedes terminarla para iniciar una nueva.',
          endGameFailed: 'No se pudo terminar la partida',
          closeRoomFailed: 'Error al cerrar la sala',
          reopenRoomFailed: 'Error al reabrir la sala',
          voteFailed: 'Error al votar',
          copyTryAgain: 'No se pudo copiar. Intenta de nuevo.',
          copyClipboard: 'Error al copiar al portapapeles',
          copyPrompt: 'Copia este enlace:',
          kickedRoomClosed: 'El creador ha cerrado la sala',
          kickedByCreator: 'Has sido expulsado de la sala por el creador',
          roomClosed: 'La sala ha sido cerrada por el creador'
        },
        turn: {
          confirmFailed: 'No se pudo confirmar tu turno'
        },
        players: {
          kickFailed: 'Error al expulsar jugador'
        },
        waiting: {
          inProgressTitle: '🎮 Partida en curso',
          statusPrefix: 'La sala está en estado: {{status}}.',
          creatorHint: 'Puedes terminarla si quieres empezar otra.',
          nonCreatorHint: 'Espera a que el creador termine la partida.',
          endGame: '🛑 Terminar partida',
          waitingCreatorActions: 'Esperando acciones del creador…',
          title: '🎭 Esperando jugadores',
          minPlayers: 'Mínimo 3 jugadores para comenzar',
          gameCategoryTitle: '📚 Categoría del juego',
          noCategory: 'Sin categoría',
          onlyCreatorCanChange: 'Solo el creador puede cambiar la categoría',
          changeCategoryLabel: 'Cambiar categoría',
          startGame: '🚀 Iniciar Juego',
          waitingAllReady: 'Esperando a que todos los jugadores estén listos...',
          waitingCreatorStart: 'Esperando a que el creador de la sala inicie el juego...'
        },
        assigningRoles: {
          title: '🎲 Asignando roles...'
        },
        roleCard: {
          tapToHide: 'Toca para ocultar tu rol',
          tapToReveal: 'Toca para revelar tu rol',
          hideRoleInfo: 'Ocultar información del rol',
          showRoleInfo: 'Mostrar información del rol',
          impostor: {
            title: '😈 ¡ERES EL IMPOSTOR!',
            remember: '⚠️ RECUERDA: La categoría es',
            categoryHint: 'Debes fingir que perteneces a esta categoría',
            instructions:
              'Escribe como si fueras parte de la categoría. Confunde a los demás simulando que encajas perfectamente.',
            tapToHide: 'Toca para ocultar esta información'
          },
          player: {
            title: '👤 Eres un jugador normal',
            categoryLead: '📝 Tu personaje pertenece a la categoría:',
            remember: 'Recuerda esto al escribir y votar',
            goal: 'Tu objetivo es encontrar al impostor',
            tapToHide: 'Toca para ocultar esta información'
          },
          generic: {
            title: '🎭 Tu rol',
            info: 'Información del rol disponible',
            tapToHide: 'Toca para ocultar esta información'
          },
          hidden: {
            title: '🤐 Información oculta',
            subtitle: 'Toca para revelar tu rol',
            hint: 'Toca cualquier parte de esta área'
          }
        },
        writing: {
          round: '📝 Ronda {{round}}',
          turnOf: 'Turno de: {{name}}',
          yourTurnTitle: '✍️ ¡ES TU TURNO!',
          yourTurnSubtitle: 'Escribe tu mensaje basado en tu rol',
          yourTurnHint: '📋 Puedes ver tu rol completo en la sección de arriba',
          placeholder: '✍️ Escribe tu mensaje aquí...',
          confirmProcessing: '⏳ Procesando turno...',
          confirmSend: '✅ Confirmar y enviar mensaje',
          otherPlayer: 'Otro jugador',
          otherIsWriting: '{{name}} está escribiendo',
          waitTurn: 'Espera tu turno para participar'
        },
        reading: {
          title: '👀 Lee los mensajes de todos',
          votingIn: 'Votación en:',
          preparing: 'Preparando...',
          preparingVotingTitle: '⏳ Preparando votación...',
          preparingVotingSubtitle: 'Organizando la información',
          didNotWrite: 'No escribió nada en esta ronda'
        },
        voting: {
          eliminatedTitle: '🗳️ Votación en curso',
          eliminatedBadge: 'HAS SIDO ELIMINADO',
          eliminatedCantVoteTitle: '❌ No puedes participar en esta votación',
          eliminatedCantVoteBody: 'Has sido eliminado del juego. Los jugadores restantes están votando.',
          activeTitle: '🗳️ ¡Vota por el impostor!',
          activeBadge: 'VOTACIÓN ACTIVA',
          processing: 'Procesando...',
          processingTitle: '⏳ Procesando resultados...',
          processingSubtitle: 'Se están contando los votos',
          voteSent: '✅ Voto enviado',
          confirmVote: '🗳️ Confirmar voto'
        },
        results: {
          title: '📊 Resultados de la votación',
          preparing: 'Preparando...',
          preparingNextTitle: '⏳ Preparando siguiente fase...',
          preparingNextSubtitle: 'Iniciando nueva ronda',
          votes: '{{count}} votos'
        },
        finished: {
          title: '🎉 ¡Juego terminado!',
          roundsCompleted: 'Rondas completadas: {{count}}',
          winnersPlayers: '✅ Ganaron los Jugadores',
          winnersImpostors: '⚠️ Ganaron los Impostores',
          wordWas: '🎯 La palabra era:',
          impostorWas: '🎭 El impostor fue:',
          impostorsWere: '🎭 Los impostores fueron:',
          backHome: '🏠 Volver al inicio',
          backWaiting: '🔁 Volver a sala de espera'
        },
        playersPanel: {
          title: '👥 Jugadores',
          you: 'Tú',
          kick: 'Expulsar a {{name}}'
        },
        connectionStatus: {
          inGame: 'En juego',
          connecting: 'Conectando...',
          ready: 'Listo',
          disconnected: 'Desconectado'
        },
        reactions: {
          send: 'Enviar reacción'
        },
        clipboard: {
          copied: '¡Enlace copiado al portapapeles!'
        },
        toast: {
          roomClosedSuccess: 'Sala cerrada exitosamente'
        }
      },

      tableSetup: {
        pageTitle: 'Modo Mesa - El Impostor Game',
        seo: {
          title: 'Modo Mesa - El Impostor Game',
          description:
            'Configura tu partida de El Impostor en modo mesa presencial. Elige el número de jugadores, impostores y la palabra secreta.'
        },
        header: {
          title: '🎲 Modo Mesa',
          subtitle: 'Configura tu partida local',
          infoTitle: 'Modo Mesa',
          infoContent:
            'El Modo Mesa está diseñado para jugar en persona con un solo dispositivo. Cada jugador verá su rol de forma secreta y luego podrán jugar sin necesidad de conexión a internet.'
        },
        playerCount: {
          label: 'Cantidad de Jugadores',
          infoTitle: 'Cantidad de Jugadores',
          infoContent: 'Selecciona cuántas personas van a jugar en esta partida. Puedes elegir entre 3 y 10 jugadores.'
        },
        playerNames: {
          title: 'Nombres de Jugadores',
          label: 'Jugador {{index}}',
          placeholder: 'Jugador {{index}}',
          defaultName: 'Jugador {{index}}'
        },
        wordMode: {
          label: 'Modo de Palabra',
          infoTitle: 'Modo de Palabra',
          infoContent:
            'Elige cómo se seleccionará la palabra secreta para el juego. Aleatorio: se elige automáticamente. Escribir: tú decides la palabra. Categoría: se elige de una categoría específica.',
          random: '🎲 Aleatorio',
          custom: '✍️ Escribir',
          category: '📂 Categoría',
          customPlaceholder: 'Escribe la palabra secreta...'
        },
        impostors: {
          label: 'Cantidad de Impostores',
          infoTitle: 'Cantidad de Impostores',
          infoContent:
            'Normal: sigue las reglas clásicas del juego (menos de la mitad son impostores). Libre: puedes elegir cualquier cantidad, incluso 0 o todos los jugadores.',
          normal: '⚖️ Normal',
          free: '🆓 Libre',
          countLabel: 'Número de impostores: {{count}}',
          maxLabel: '(máximo: {{max}})'
        },
        actions: {
          starting: 'Iniciando...',
          start: '🚀 Iniciar Juego',
          backHome: '← Volver al Inicio'
        },
        errors: {
          customWordRequired: 'Debes escribir una palabra para el modo personalizado',
          categoryRequired: 'Debes seleccionar una categoría',
          startFailed: 'Ocurrió un error al iniciar el juego. Por favor, intenta nuevamente.'
        }
      },

      tableGame: {
        pageTitle: 'Jugando - Modo Mesa',
        seo: {
          title: 'Jugando - Modo Mesa',
          description: 'Partida en curso del modo mesa de El Impostor. Descubre tu rol y participa en la deducción social.'
        },
        progress: {
          playerOf: 'Jugador {{current}} de {{total}}'
        },
        turnOf: 'Turno de {{name}}',
        tapCardHint: 'Toca la carta para ver tu rol',
        cardBack: {
          tapToReveal: 'Toca para revelar'
        },
        cardFront: {
          impostor: 'IMPOSTOR',
          player: 'JUGADOR',
          yourWord: 'Tu palabra es:',
          impostorHint: '¡Descubre la palabra sin ser descubierto!'
        },
        next: {
          finish: '✅ Finalizar Asignación',
          passTo: '➡️ Pasar a {{name}}',
          revealFirst: 'Primero revela tu carta para ver tu rol'
        },
        instructions: {
          privacy: '💡 Asegúrate de que nadie más vea la pantalla antes de revelar tu rol'
        },
        modalNext: {
          title: 'Turno de {{name}}',
          body: 'Pasa el dispositivo a {{name}} y presiona continuar cuando esté listo.',
          continue: 'Continuar'
        },
        modalComplete: {
          title: '¡Todos listos!',
          body: 'Todos los jugadores tienen sus roles asignados. ¡Que comience el juego!',
          newGame: '🔄 Nueva Partida',
          goHome: '🏠 Ir al Inicio'
        }
      },

      infoModal: {
        closeAria: 'Cerrar',
        howToPlay: '¿Cómo se juega?',
        moreInfo: '📖 Más información',
        close: 'Cerrar'
      }
    }
  },
  en: {
    translation: {
      common: {
        toggleMenu: 'Toggle menu',
        switchToEn: 'Switch to English',
        switchToEs: 'Switch to Spanish',
        switchToPt: 'Switch to Portuguese',
        backHomeAria: 'Back to home',
        confirm: 'Confirm',
        cancel: 'Cancel',
        playNow: 'Play now',
        toggleTheme: 'Toggle light/dark mode',
        themeDark: 'Dark',
        themeLight: 'Light',
        close: 'Close',
        understood: 'Got it',
        information: 'Information',
        leave: 'Leave',
        stay: 'Stay',
        loading: 'Loading...',
        menu: 'Menu'
      },
      cookieConsent: {
        textPrefix:
          'We use our own and third-party cookies to improve your experience, analyze traffic, and show personalized ads. By continuing to browse, you accept our',
        privacyPolicyLinkText: 'Privacy Policy',
        accept: 'Accept'
      },
      nav: {
        home: 'Home',
        blog: 'Blog',
        contact: 'Contact',
        play: 'Play'
      },
      siteFooter: {
        brand: 'Impostor Game',
        description: 'Find the impostor in this exciting online social game.',
        linksTitle: 'Links',
        links: {
          blog: 'Blog',
          about: 'About',
          contact: 'Contact'
        },
        legalTitle: 'Legal',
        legal: {
          privacy: 'Privacy',
          terms: 'Terms'
        },
        followTitle: 'Follow us',
        followText: 'Stay up to date with the latest news and game updates.',
        rightsReserved: 'All rights reserved.',
        madeWithLove: 'Built with ❤️ by RurayLabs'
      },
      home: {
        pageTitle: 'Impostor Game - Online Social Party Game',
        seo: {
          title: 'Impostor Game - Online Social Party Game',
          description:
            'Impostor Game ▷ FREE online social deduction game. Find the impostor while playing with friends. Real-time multiplayer and an in-person table mode. Play now!'
          ,
          keywords:
            'impostor game, social deduction game, online party game, play with friends, free online game, real-time multiplayer, table mode'
        },
        brand: 'Impostor Game',
        moreInfoTitle: 'How does it work?',
        hero: {
          tagline: 'Find the impostor before it is too late!'
        },
        online: {
          title: '🌐 Online Multiplayer',
          moreInfoAria: 'More information about online mode',
          description: 'Connect with friends from anywhere in real time',
          createRoom: '🏠 Create Room',
          joinRoom: '🚪 Join Room',
          modal: {
            title: 'Online Multiplayer',
            description: 'Connect with friends from anywhere in the world in real time',
            steps: [
              'Create a new room or join with a 6-character code',
              'Wait for all players to connect from their devices',
              'Roles are assigned automatically (players and impostors)',
              'Each round, write messages and discuss with others',
              'Vote to eliminate who you think is the impostor',
              'Players win if they eliminate the impostor!'
            ]
          }
        },
        divider: {
          or: 'OR'
        },
        table: {
          title: '🎲 Table Mode',
          moreInfoAria: 'More information about table mode',
          description: 'One device, maximum face-to-face interaction',
          play: '🎲 Play Table Mode',
          modal: {
            title: 'Table Mode',
            description: 'Enjoy face-to-face interaction without digital distractions',
            steps: [
              'Gather all players in the same place',
              'Enter names on a single shared device',
              "Each player sees their role privately (don’t peek!)",
              'Discuss face to face who the impostor is',
              'Vote by raising hands or using the device',
              'Enjoy the in-person interaction and discover the impostor!'
            ]
          }
        },
        links: {
          contact: 'Contact',
          about: 'About',
          guide: 'Guide & Tutorial'
        },
        recommended: {
          pages: 'Recommended Pages',
          games: 'Recommended Games',
          desc: {
            brainTwins: 'The game where minds sync up',
            imgParser: 'Transform your images in seconds',
            dailyQuiz: 'How much do you know? Test your mind!'
          }
        },
        footer: {
          finePrint: 'Best with 3–10 players • Real-time • Multi-room',
          recommended: 'Recommended games:'
        }
      },
      create: {
        pageTitle: 'Create Room - Impostor Game',
        seo: {
          title: 'Create Room - Impostor Game',
          description:
            'Create a private room to play Impostor Game with friends in real time. Configure players and impostors.'
        },
        backHome: '← Back to home',
        header: {
          title: '🏠 Create New Room',
          subtitle: 'Set up your room and invite friends'
        },
        errors: {
          createFailed: 'Error creating the room. Please try again.',
          nameRequiredToJoinPublic: 'Enter your name above to join a public room.'
        },
        form: {
          nameLabel: 'Your name',
          namePlaceholder: 'Enter your name',
          passwordToggleLabel: 'Protect with password',
          passwordToggleHelp: 'Only people who know it can join',
          passwordLabel: 'Room password',
          passwordPlaceholder: 'Enter a password',
          minPlayersLabel: 'Min players',
          maxPlayersLabel: 'Max players',
          characterLabel: 'Choose a character'
        },
        activeRooms: {
          title: 'Your active rooms:',
          goToRoom: 'Go to room',
          hint: '💡 You can enter these rooms and close them to free space'
        },
        submit: {
          creating: 'Creating room...',
          create: '🚀 Create Room'
        },
        footer: {
          autoCloseNote: 'The room will close automatically after 2 hours of inactivity'
        }
      },
      join: {
        pageTitle: 'Join Room - Impostor Game',
        seo: {
          title: 'Join Room - Impostor Game',
          description: 'Join an Impostor Game room with a 6-character code and play with friends in real time.'
        },
        backHome: '← Back to home',
        header: {
          title: '🚪 Join Room',
          subtitle: 'Enter the code to join the game'
        },
        errors: {
          roomNotFoundWithHint: 'Room not found. Check the code.',
          roomNotFound: 'Room not found.',
          checkRoomFailed: 'Error checking the room.',
          wrongPassword: 'Incorrect password.',
          joinFailed: 'Error joining the room. Please try again.',
          publicRoomsFailed: 'Could not load public rooms.'
        },
        form: {
          roomCodeLabel: 'Room code',
          roomCodeHelp: '6-character code provided by the host',
          nameLabel: 'Your name',
          namePlaceholder: 'Enter your name',
          characterLabel: 'Choose a character',
          passwordLabel: 'Room password',
          passwordPlaceholder: 'Enter the password'
        },
        roomInfo: {
          found: '✅ Room found',
          createdBy: 'Created by:',
          statusLabel: 'Status:',
          statusWaiting: 'Waiting for players',
          statusPlaying: 'In game',
          requiresPassword: '🔒 Password required',
          playersCount: '{{current}}/{{max}} players'
        },
        submit: {
          joining: 'Joining...',
          playingInProgress: '🎮 Game in progress',
          joinRoom: '🚀 Join Room'
        },
        noCode: {
          prefix: "Don't have a code? Ask the host or",
          createNew: 'create a new one'
        },
        publicRooms: {
          title: '🌐 Active public rooms',
          refreshing: 'Refreshing...',
          refresh: '🔄 Refresh',
          loading: 'Loading public rooms...',
          emptyTitle: 'No public rooms available right now.',
          emptySubtitle: 'Be the first to create one!',
          hint: '💡 Rooms update automatically every 30 seconds',
          newBadge: '✨ New',
          hostFallback: 'Host',
          full: '🔒 Full',
          joinFromList: '🚀 Join',
          timeAgo: {
            justCreated: 'Just created',
            oneMinute: '1 min ago',
            minutes: '{{minutes}} min ago',
            hours: '{{hours}} h ago'
          }
        }
      },
      contactPage: {
        pageTitle: 'Contact - Impostor Game',
        seo: {
          title: 'Contact - Impostor Game',
          description: 'Contact us for business inquiries or send suggestions and recommendations to improve Impostor Game.'
        },
        header: {
          title: '📧 Contact',
          subtitle: 'Business inquiry or suggestion? We’d love to hear from you!'
        },
        tabs: {
          business: '💼 Business',
          recommendations: '💡 Suggestions'
        },
        messages: {
          success: 'Message sent successfully! We will get back to you soon.',
          error: 'There was an error sending your message. Please try again.'
        },
        business: {
          title: 'Business inquiries',
          description:
            'Have a commercial proposal, want to collaborate, or need more information about our services? Fill out the form and we will get back to you.',
          emailLabel: 'Email *',
          emailPlaceholder: 'you@email.com',
          nameLabel: 'Full name *',
          namePlaceholder: 'Your full name',
          subjectLabel: 'Subject *',
          subjectPlaceholder: 'What is this about?',
          descriptionLabel: 'Description *',
          descriptionPlaceholder: 'Describe your inquiry, proposal, or request in detail...',
          submitSending: '📤 Sending...',
          submit: '📤 Send inquiry'
        },
        recommendations: {
          title: 'Suggestions & feedback',
          infoTitle: 'Anonymous submission',
          infoText:
            'You can submit requests, improvements, bug reports, or any suggestion completely anonymously. No personal information is required.',
          description:
            'Your feedback is very valuable to us. Help us improve the game by sharing ideas, reporting issues, or suggesting new features.',
          textareaLabel: 'Your suggestion or feedback *',
          textareaPlaceholder:
            'Share your suggestion, improvement idea, bug report, feature request, or any comment you have about the game...',
          examples:
            'Examples: "It would be great to add more characters", "I found a bug when...", "The game could improve if..."',
          privacyTitle: '🔒 Privacy',
          privacyText:
            'This suggestion will be sent anonymously. We do not store any personal information that could identify you. We simply want to hear your ideas to make the game better.',
          submitSending: '📨 Sending...',
          submit: '📨 Send suggestion'
        },
        footer: {
          preferOtherPrefix: 'Prefer another way to reach us? Find us on social media or send an email to'
        }
      },
      notFound: {
        seo: {
          title: 'Page not found - 404 | Impostor Game',
          description: "The page you're looking for doesn't exist. Go back home to play Impostor Game online."
        },
        title: 'Page Not Found',
        description: "Sorry, the page you're looking for doesn't exist or has been moved.",
        backHome: 'Back to Home',
        goBlog: 'Go to Blog'
      },

      blogGuide: {
        brand: 'Impostor Game',
        pageTitle: 'How to play Impostor Game | Complete Guide & Tutorial',
        metaDescription:
          'Learn how to play Impostor Game: rules, roles, rounds, voting, and strategies. A complete step-by-step guide for new players.',
        nav: {
          home: 'Home',
          createRoom: 'Create room',
          join: 'Join'
        },
        title: 'How to play Impostor Game: complete guide & tutorial',
        intro:
          'Welcome to the official Impostor Game guide. Here you will learn what the game is, how to play step by step, and all the available options so you can make the most of it—whether with friends, in class, or at events.',
        whatIs: {
          heading: 'What is Impostor Game?',
          paragraph:
            'Impostor Game is a round-based social game inspired by deduction dynamics. In each match there are regular players and one or more impostors. Players write messages, debate, and at the end of each round vote to eliminate who they think is the impostor.',
          bullets: ['Great for 3 to 10 players', 'Fast real-time matches', 'Perfect for icebreakers, classes, and streaming']
        },
        howToPlay: {
          heading: 'How to play (step by step)',
          steps: [
            'Create a new room or join with a shared code.',
            'Roles are assigned automatically: players and impostors.',
            'Each round, write your message and read everyone else’s.',
            'Debate with the group and use logic to spot inconsistencies.',
            'Vote to eliminate a suspect at the end of the round.',
            'The team that meets its goal wins: players if they eliminate the impostor, impostors if they remain undetected.'
          ],
          ctaCreate: 'Create a room now',
          ctaJoin: 'Join a room'
        },
        roles: {
          heading: 'Game roles',
          player: 'Player: your goal is to identify suspicious behavior, ask smart questions, and vote strategically.',
          impostor: 'Impostor: your goal is to blend in, create doubt, and avoid being discovered until the end.'
        },
        mechanics: {
          heading: 'Game mechanics',
          bullets: [
            'Timed rounds for writing and debating.',
            'Anonymous voting with automatic counting.',
            'Reveal who was eliminated and move to the next round.',
            'Multi-room support for multiple groups.'
          ]
        },
        options: {
          heading: 'Options and settings',
          bullets: [
            'Recommended player count: 3 to 10.',
            'Roles are assigned automatically to balance the match.',
            'Round-based chat pacing.',
            'End-of-round voting with automatic tie-breaks.'
          ]
        },
        tips: {
          heading: 'Tips to win',
          bullets: ['Watch language and contradictions.', 'Ask open-ended questions to force explanations.', 'As impostor, keep messages believable and avoid overdoing it.']
        },
        faq: {
          heading: 'FAQ',
          items: [
            { q: 'Do I need an account?', a: 'No—just a name to enter a room.' },
            { q: 'Does it work on mobile?', a: 'Yes, it is optimized for mobile devices.' },
            { q: 'How long does a match last?', a: 'About 5–15 minutes, depending on player count.' }
          ]
        },
        ready: {
          title: 'Ready to play?',
          subtitle: 'Create a room and invite your friends in seconds.',
          create: 'Create room',
          join: 'Join room',
          backHome: 'Back to home'
        },
        footer: 'Impostor Game • Tutorial and game guide'
      },

      blogOnline: {
        brand: 'Impostor Game',
        pageTitle: 'Online Multiplayer Mode - Impostor Game',
        metaDescription:
          'Play Impostor Game in online multiplayer mode with friends from anywhere. Real-time connection, instant voting, and guaranteed fun.',
        nav: {
          home: 'Home',
          blog: 'Blog',
          createRoom: 'Create room'
        },
        title: '🌐 Online Multiplayer Mode',
        subtitle: 'Play with friends from anywhere in real time',
        whatIs: {
          heading: 'What is online mode?',
          paragraph:
            'Impostor Game online multiplayer mode lets you play with friends no matter where they are. Just share a room code and everyone can join from their phones or computers.',
          benefitTitle: '✨ Main benefit:',
          benefitText:
            'Connect with friends, family, or coworkers from any location. Great for virtual hangouts, online classes, or just having fun remotely.'
        },
        features: {
          heading: 'Key features',
          items: [
            'Real-time connection: Everyone sees updates instantly',
            'Multi-room: Several matches can run at the same time',
            'No installation: You only need a web browser',
            'Mobile-friendly: Play from any device',
            'Round-based chat: Debate strategically each phase',
            'Anonymous voting: Nobody knows who voted for whom until the end'
          ]
        },
        steps: {
          heading: 'How to play (step by step)',
          items: [
            'Create or join a room: The host creates a room and shares the code',
            'Wait for players: Others join using the 6-character code',
            'Role assignment: The system automatically assigns roles (players and impostors)',
            'Game rounds: Each round, write messages and read others',
            'Debate: Use logic to spot suspicious behavior',
            'Vote: At the end of each round, vote to eliminate a suspect',
            'Victory: Players win by eliminating the impostor; impostors win by staying hidden'
          ],
          ctaCreate: 'Create a room now',
          ctaJoin: 'Join a room'
        },
        useCases: {
          heading: 'Ideal use cases',
          items: [
            { title: 'Virtual meetings', description: 'Perfect as an icebreaker for work calls or online events' },
            { title: 'Online education', description: 'A dynamic tool for virtual classes and critical thinking' },
            { title: 'Gaming with friends', description: 'Organize matches with friends in different cities or countries' },
            { title: 'Streaming', description: 'Great for streamers who want to interact with their community' }
          ]
        },
        tips: {
          heading: 'Tips for online mode',
          items: [
            'Use a parallel voice/video call: Combine with Discord, Zoom, or Meet for immersion',
            'Set clear rules: Decide debate time and guidelines before starting',
            'Keep groups balanced: 4–8 players is a sweet spot',
            'Stable connection: Make sure your internet is reliable',
            'Be respectful: Keep it friendly and fun for everyone'
          ]
        },
        faq: {
          heading: 'FAQ',
          items: [
            { q: 'Do I need an account to play?', a: 'No—just a name to enter the room.' },
            { q: 'How many players can join?', a: 'We recommend 3 to 10 players for the best experience.' },
            { q: 'Does it work on mobile?', a: 'Yes, it is fully optimized for mobile devices.' },
            { q: 'How long does a match last?', a: 'About 5–15 minutes, depending on players and rounds.' },
            { q: 'Can I play privately?', a: 'Yes—only share the code with the people you want to invite.' }
          ]
        },
        ready: {
          title: 'Ready to play online?',
          subtitle: 'Create a room and invite your friends in seconds. Fun is one click away!',
          create: 'Create room',
          join: 'Join room',
          backHome: 'Back to home'
        },
        footer: 'Impostor Game • Online Multiplayer Mode'
      },

      blogTable: {
        brand: 'Impostor Game',
        pageTitle: 'Table Mode - Impostor Game',
        metaDescription:
          'Play Impostor Game in table mode for in-person gatherings. One device, face-to-face fun, no need for multiple connections.',
        nav: {
          home: 'Home',
          blog: 'Blog',
          playTable: 'Play Table Mode'
        },
        title: '🎲 Table Mode',
        subtitle: 'One device, maximum face-to-face interaction',
        whatIs: {
          heading: 'What is table mode?',
          paragraph:
            'Impostor Game table mode is designed for in-person gatherings using a single device. Perfect for parties, family get-togethers, classrooms, or any event where everyone is in the same place.',
          benefitTitle: '✨ Main benefit:',
          benefitText:
            'Enjoy face-to-face interaction without digital distractions. You only need one device (tablet, laptop, or screen) and everyone can participate together. Great for strengthening social bonds and creating memorable moments.'
        },
        features: {
          heading: 'Key features',
          items: [
            'One device: Players don’t need their own phones',
            'Individual role reveal: Each player sees their role privately',
            'Face-to-face discussion: In-person debate for better immersion',
            'In-person voting: Vote by hands or using the shared device',
            'No internet required: Once loaded, it can work offline',
            'Great for local groups: Ideal for in-person events'
          ]
        },
        steps: {
          heading: 'How to play (step by step)',
          items: [
            'Set up the game: Enter all player names on one device',
            'Distribute roles: The system assigns secret roles to each player',
            'Reveal roles: Each player views their role privately',
            'In-person debate: Discuss who might be the impostor',
            'Voting: Each player votes by pointing or using the shared device',
            'Reveal: The eliminated player and their role are revealed',
            'Victory: The game continues until the impostor is found or impostors win'
          ],
          ctaPlay: 'Play Table Mode'
        },
        useCases: {
          heading: 'Ideal use cases',
          items: [
            { title: 'Parties and gatherings', description: 'Perfect to energize birthdays, family reunions, or social events' },
            { title: 'Classrooms and education', description: 'An educational tool to build communication and critical thinking' },
            { title: 'Team building', description: 'A bonding activity for teams and workplace dynamics' },
            { title: 'Cafés and social spaces', description: 'Great for groups of friends meeting in person' }
          ]
        },
        advantages: {
          heading: 'Advantages of table mode',
          items: [
            { title: 'Body language', description: 'Observe gestures, looks, and reactions in real time' },
            { title: 'Cost-effective', description: 'No need for multiple devices or connections' },
            { title: 'More immersive', description: 'Face-to-face interaction makes it more exciting' },
            { title: 'Quick setup', description: 'Start playing in under a minute' }
          ]
        },
        tips: {
          heading: 'Tips for table mode',
          items: [
            'Use a big screen: Best experience on a tablet, laptop, or TV',
            'Keep roles private: Make sure others don’t peek',
            'Set time limits: Decide how long each debate round lasts',
            'Create the right setup: Sit in a circle so everyone can see each other',
            'Respect and fun: Keep it friendly and enjoyable'
          ]
        },
        comparison: {
          heading: 'Table vs Online: which to choose?',
          headers: {
            aspect: 'Aspect',
            table: 'Table Mode',
            online: 'Online Mode'
          },
          rows: [
            { aspect: 'Devices needed', table: '1 device total', online: '1 per player' },
            { aspect: 'Location', table: 'Same place', online: 'Anywhere' },
            { aspect: 'Interaction', table: 'Face-to-face', online: 'Virtual/text' },
            { aspect: 'Best for', table: 'Parties, classrooms, in-person gatherings', online: 'Friends at a distance, remote work' }
          ]
        },
        faq: {
          heading: 'FAQ',
          items: [
            { q: 'Do I need internet for table mode?', a: 'Only to load the page initially. Once loaded, it can work offline.' },
            { q: 'How many players can participate?', a: 'We recommend 3 to 10 players for the best experience.' },
            { q: 'Which device is best?', a: 'A tablet or laptop is ideal, but phones also work.' },
            { q: 'How do roles stay secret?', a: 'The game reveals roles individually and other players should not look.' },
            { q: 'Can I use table mode in class?', a: 'Absolutely! It’s a great educational tool for social and cognitive skills.' }
          ]
        },
        ready: {
          title: 'Ready to play table mode?',
          subtitle: 'Gather your group, prepare the device, and let the face-to-face fun begin!',
          play: 'Play Table Mode',
          backHome: 'Back to home'
        },
        footer: 'Impostor Game • Table Mode'
      },

      gamesShowcase: {
        loading: 'Loading games…',
        empty: 'No games available',
        title: 'Discover Our Games',
        subtitle: 'Explore our collection and choose your next adventure'
      },

      categorySelector: {
        selectPlaceholder: 'Select a category',
        searchPlaceholder: '🔍 Search category...',
        noResults: 'No categories found',
        charactersCount: '{{count}} characters',
        showMore: 'Show more ({{count}} remaining)',
        showing: 'Showing {{visible}} of {{total}} categories',
        filteredSuffix: '(filtered from {{all}} total)'
      },

      gameRoom: {
        seo: {
          title: 'Room {{roomCode}} - Impostor Game',
          description:
            'Real-time Impostor Game room. Join the social deduction and find out who the impostor is.'
        },
        header: {
          title: 'Room {{roomCode}}',
          playersConnected: '{{count}} players connected',
          yourCharacterAlt: 'Your character',
          roomArtAlt: 'Room artwork',
          copyLink: '📋 Copy link',
          closeRoom: '🔒 Close room',
          leave: '🚪 Leave'
        },
        websocket: {
          connected: 'Connected in real time',
          disconnected: 'Disconnected - Retrying...'
        },
        systemMessage: {
          default: 'System notification'
        },
        reconnecting: {
          title: '🔄 Reconnecting',
          restoring: 'Restoring your session...',
          retrying: 'Retrying connection ({{current}}/{{max}})...',
          updatingSession: 'Updating session...',
          help: 'If this takes too long, try reloading the page',
          brand: '🎭 Impostor Game',
          overlayTitle: 'Reconnecting to the game',
          attempt: 'Attempt {{current}} of {{max}}',
          server: 'Re-establishing server connection...',
          overlayFooter: 'Impostor Game - Multiplayer'
        },
        confirm: {
          beforeUnload: 'Are you sure you want to leave? The game is in progress.',
          leaveTitle: 'Leave the game?',
          leaveMessage: 'Are you sure you want to leave? The game is in progress and you will lose your progress.',
          closeTitle: 'Close room?',
          closeMessage:
            'Are you sure you want to close the room? All players will be disconnected and the room will become inactive. This will free space for new rooms.'
        },
        errors: {
          loadRoomInfo: 'Error loading room information',
          joinFailed: 'Could not join the room',
          reconnectFailed: 'Could not reconnect',
          connectionError: '⚠️ Connection error: {{message}}. Try reloading the page manually.',
          startGame: 'Error starting the game',
          roomNotWaiting:
            "The room isn't in waiting state. It looks like a game is in progress. If you're the host, you can end it to start a new one.",
          endGameFailed: 'Could not end the game',
          closeRoomFailed: 'Error closing the room',
          reopenRoomFailed: 'Error reopening the room',
          voteFailed: 'Error voting',
          copyTryAgain: 'Could not copy. Please try again.',
          copyClipboard: 'Error copying to clipboard',
          copyPrompt: 'Copy this link:',
          kickedRoomClosed: 'The host has closed the room',
          kickedByCreator: 'You have been kicked from the room by the host',
          roomClosed: 'The room has been closed by the host'
        },
        turn: {
          confirmFailed: 'Could not confirm your turn'
        },
        players: {
          kickFailed: 'Error kicking player'
        },
        waiting: {
          inProgressTitle: '🎮 Game in progress',
          statusPrefix: 'Room status: {{status}}.',
          creatorHint: 'You can end it if you want to start another one.',
          nonCreatorHint: 'Wait for the host to end the game.',
          endGame: '🛑 End game',
          waitingCreatorActions: 'Waiting for the host…',
          title: '🎭 Waiting for players',
          minPlayers: 'Minimum 3 players to start',
          gameCategoryTitle: '📚 Game category',
          noCategory: 'No category',
          onlyCreatorCanChange: 'Only the host can change the category',
          changeCategoryLabel: 'Change category',
          startGame: '🚀 Start game',
          waitingAllReady: 'Waiting for all players to be ready...',
          waitingCreatorStart: 'Waiting for the host to start the game...'
        },
        assigningRoles: {
          title: '🎲 Assigning roles...'
        },
        roleCard: {
          tapToHide: 'Tap to hide your role',
          tapToReveal: 'Tap to reveal your role',
          hideRoleInfo: 'Hide role info',
          showRoleInfo: 'Show role info',
          impostor: {
            title: '😈 YOU ARE THE IMPOSTOR!',
            remember: '⚠️ REMEMBER: The category is',
            categoryHint: 'You must pretend you belong to this category',
            instructions:
              'Write as if you are part of the category. Confuse others by pretending you fit perfectly.',
            tapToHide: 'Tap to hide this info'
          },
          player: {
            title: '👤 You are a regular player',
            categoryLead: '📝 Your character belongs to:',
            remember: 'Remember this when writing and voting',
            goal: 'Your goal is to find the impostor',
            tapToHide: 'Tap to hide this info'
          },
          generic: {
            title: '🎭 Your role',
            info: 'Role info available',
            tapToHide: 'Tap to hide this info'
          },
          hidden: {
            title: '🤐 Hidden information',
            subtitle: 'Tap to reveal your role',
            hint: 'Tap anywhere in this area'
          }
        },
        writing: {
          round: '📝 Round {{round}}',
          turnOf: 'Turn: {{name}}',
          yourTurnTitle: "✍️ IT'S YOUR TURN!",
          yourTurnSubtitle: 'Write your message based on your role',
          yourTurnHint: '📋 You can see your full role above',
          placeholder: '✍️ Write your message here...',
          confirmProcessing: '⏳ Processing turn...',
          confirmSend: '✅ Confirm & send message',
          otherPlayer: 'Another player',
          otherIsWriting: '{{name}} is writing',
          waitTurn: 'Wait for your turn to participate'
        },
        reading: {
          title: '👀 Read everyone’s messages',
          votingIn: 'Voting in:',
          preparing: 'Preparing...',
          preparingVotingTitle: '⏳ Preparing voting...',
          preparingVotingSubtitle: 'Organizing information',
          didNotWrite: 'Did not write anything this round'
        },
        voting: {
          eliminatedTitle: '🗳️ Voting in progress',
          eliminatedBadge: 'YOU HAVE BEEN ELIMINATED',
          eliminatedCantVoteTitle: '❌ You cannot vote in this round',
          eliminatedCantVoteBody: 'You have been eliminated. Remaining players are voting.',
          activeTitle: '🗳️ Vote for the impostor!',
          activeBadge: 'VOTING',
          processing: 'Processing...',
          processingTitle: '⏳ Processing results...',
          processingSubtitle: 'Counting votes',
          voteSent: '✅ Vote sent',
          confirmVote: '🗳️ Confirm vote'
        },
        results: {
          title: '📊 Voting results',
          preparing: 'Preparing...',
          preparingNextTitle: '⏳ Preparing next phase...',
          preparingNextSubtitle: 'Starting next round',
          votes: '{{count}} votes'
        },
        finished: {
          title: '🎉 Game over!',
          roundsCompleted: 'Rounds completed: {{count}}',
          winnersPlayers: '✅ Players win',
          winnersImpostors: '⚠️ Impostors win',
          wordWas: '🎯 The word was:',
          impostorWas: '🎭 The impostor was:',
          impostorsWere: '🎭 The impostors were:',
          backHome: '🏠 Back to home',
          backWaiting: '🔁 Back to waiting room'
        },
        playersPanel: {
          title: '👥 Players',
          you: 'You',
          kick: 'Kick {{name}}'
        },
        connectionStatus: {
          inGame: 'In game',
          connecting: 'Connecting...',
          ready: 'Ready',
          disconnected: 'Disconnected'
        },
        reactions: {
          send: 'Send reaction'
        },
        clipboard: {
          copied: 'Link copied to clipboard!'
        },
        toast: {
          roomClosedSuccess: 'Room closed successfully'
        }
      },

      tableSetup: {
        pageTitle: 'Table Mode - Impostor Game',
        seo: {
          title: 'Table Mode - Impostor Game',
          description:
            'Set up your in-person Impostor Game in table mode. Choose number of players, impostors, and the secret word.'
        },
        header: {
          title: '🎲 Table Mode',
          subtitle: 'Set up your local game',
          infoTitle: 'Table Mode',
          infoContent:
            'Table Mode is designed to play in person with a single device. Each player will privately view their role and then you can play without needing an internet connection.'
        },
        playerCount: {
          label: 'Number of Players',
          infoTitle: 'Number of Players',
          infoContent: 'Select how many people will play. You can choose between 3 and 10 players.'
        },
        playerNames: {
          title: 'Player Names',
          label: 'Player {{index}}',
          placeholder: 'Player {{index}}',
          defaultName: 'Player {{index}}'
        },
        wordMode: {
          label: 'Word Mode',
          infoTitle: 'Word Mode',
          infoContent:
            'Choose how the secret word will be selected. Random: chosen automatically. Custom: you choose the word. Category: chosen from a specific category.',
          random: '🎲 Random',
          custom: '✍️ Custom',
          category: '📂 Category',
          customPlaceholder: 'Enter the secret word...'
        },
        impostors: {
          label: 'Number of Impostors',
          infoTitle: 'Number of Impostors',
          infoContent:
            'Normal: follows classic rules (less than half are impostors). Free: choose any amount, even 0 or all players.',
          normal: '⚖️ Normal',
          free: '🆓 Free',
          countLabel: 'Number of impostors: {{count}}',
          maxLabel: '(max: {{max}})'
        },
        actions: {
          starting: 'Starting...',
          start: '🚀 Start game',
          backHome: '← Back to Home'
        },
        errors: {
          customWordRequired: 'You must enter a word for custom mode',
          categoryRequired: 'You must select a category',
          startFailed: 'An error occurred while starting the game. Please try again.'
        }
      },

      tableGame: {
        pageTitle: 'Playing - Table Mode',
        seo: {
          title: 'Playing - Table Mode',
          description: 'Ongoing table mode game. Reveal your role and join the social deduction.'
        },
        progress: {
          playerOf: 'Player {{current}} of {{total}}'
        },
        turnOf: "{{name}}'s turn",
        tapCardHint: 'Tap the card to see your role',
        cardBack: {
          tapToReveal: 'Tap to reveal'
        },
        cardFront: {
          impostor: 'IMPOSTOR',
          player: 'PLAYER',
          yourWord: 'Your word is:',
          impostorHint: 'Figure out the word without getting caught!'
        },
        next: {
          finish: '✅ Finish Assignment',
          passTo: '➡️ Pass to {{name}}',
          revealFirst: 'Reveal your card first to see your role'
        },
        instructions: {
          privacy: '💡 Make sure nobody else sees the screen before revealing your role'
        },
        modalNext: {
          title: "{{name}}'s turn",
          body: 'Pass the device to {{name}} and press continue when ready.',
          continue: 'Continue'
        },
        modalComplete: {
          title: 'Everyone is ready!',
          body: 'All players have their roles. Let the game begin!',
          newGame: '🔄 New Game',
          goHome: '🏠 Go Home'
        }
      },

      infoModal: {
        closeAria: 'Close',
        howToPlay: 'How to play',
        moreInfo: '📖 More information',
        close: 'Close'
      }
    }
  },
  pt: {
    translation: {
      common: {
        toggleMenu: 'Alternar menu',
        switchToEn: 'Mudar para inglês',
        switchToEs: 'Mudar para espanhol',
        switchToPt: 'Mudar para português',
        backHomeAria: 'Voltar ao início',
        confirm: 'Confirmar',
        cancel: 'Cancelar',
        playNow: 'Jogar agora',
        toggleTheme: 'Alternar modo claro/escuro',
        themeDark: 'Escuro',
        themeLight: 'Claro',
        close: 'Fechar',
        understood: 'Entendi',
        information: 'Informações',
        leave: 'Sair',
        stay: 'Ficar',
        loading: 'Carregando...',
        menu: 'Menu'
      },
      cookieConsent: {
        textPrefix:
          'Usamos cookies próprios e de terceiros para melhorar sua experiência, analisar o tráfego e mostrar publicidade personalizada. Ao continuar navegando, você aceita nossa',
        privacyPolicyLinkText: 'Política de Privacidade',
        accept: 'Aceitar'
      },
      nav: {
        home: 'Início',
        blog: 'Blog',
        contact: 'Contato',
        play: 'Jogar'
      },
      siteFooter: {
        brand: 'Impostor Game',
        description: 'Encontre o impostor neste emocionante jogo social online.',
        linksTitle: 'Links',
        links: {
          blog: 'Blog',
          about: 'Sobre',
          contact: 'Contato'
        },
        legalTitle: 'Legal',
        legal: {
          privacy: 'Privacidade',
          terms: 'Termos'
        },
        followTitle: 'Siga-nos',
        followText: 'Fique por dentro das últimas novidades e atualizações do jogo.',
        rightsReserved: 'Todos os direitos reservados.',
        madeWithLove: 'Feito com ❤️ por RurayLabs'
      },
      home: {
        pageTitle: 'Impostor Game - Jogo Social Online',
        seo: {
          title: 'Impostor Game - Jogo Social Online',
          description:
            'Impostor Game ▷ Jogo online GRATUITO de dedução social. Descubra quem é o impostor jogando com amigos. Multijogador em tempo real e modo mesa presencial. Jogue agora!'
          ,
          keywords:
            'impostor game, jogo de dedução social, jogo online, jogar com amigos, jogo grátis, multijogador em tempo real, modo mesa'
        },
        brand: 'Impostor Game',
        moreInfoTitle: 'Como funciona?',
        hero: {
          tagline: 'Encontre o impostor antes que seja tarde demais!'
        },
        online: {
          title: '🌐 Multijogador Online',
          moreInfoAria: 'Mais informações sobre o modo online',
          description: 'Conecte-se com amigos de qualquer lugar em tempo real',
          createRoom: '🏠 Criar Sala',
          joinRoom: '🚪 Entrar na Sala',
          modal: {
            title: 'Multijogador Online',
            description: 'Conecte-se com amigos de qualquer lugar do mundo em tempo real',
            steps: [
              'Crie uma sala nova ou entre com um código de 6 caracteres',
              'Espere todos os jogadores se conectarem em seus dispositivos',
              'Os papéis são atribuídos automaticamente (jogadores e impostores)',
              'Em cada rodada, escreva mensagens e debata com os outros',
              'Vote para eliminar quem você acha que é o impostor',
              'Os jogadores vencem se eliminarem o impostor!'
            ]
          }
        },
        divider: {
          or: 'OU'
        },
        table: {
          title: '🎲 Modo Mesa',
          moreInfoAria: 'Mais informações sobre o modo mesa',
          description: 'Um dispositivo, máxima interação presencial',
          play: '🎲 Jogar no Modo Mesa',
          modal: {
            title: 'Modo Mesa',
            description: 'Aproveite a interação cara a cara sem distrações digitais',
            steps: [
              'Reúna todos os jogadores no mesmo lugar',
              'Digite os nomes em um único dispositivo compartilhado',
              'Cada jogador vê seu papel de forma privada (não espiem!)',
              'Debatam cara a cara quem é o impostor',
              'Votem levantando a mão ou usando o dispositivo',
              'Aproveite a interação presencial e descubra o impostor!'
            ]
          }
        },
        links: {
          contact: 'Contato',
          about: 'Sobre',
          guide: 'Guia e Tutorial'
        },
        recommended: {
          pages: 'Páginas Recomendadas',
          games: 'Jogos Recomendados',
          desc: {
            brainTwins: 'O jogo onde as mentes se sincronizam',
            imgParser: 'Transforme suas imagens em segundos',
            dailyQuiz: 'Quanto você sabe? Teste sua mente!'
          }
        },
        footer: {
          finePrint: 'Melhor com 3–10 jogadores • Em tempo real • Multissala',
          recommended: 'Jogos recomendados:'
        }
      },
      create: {
        pageTitle: 'Criar Sala - Impostor Game',
        seo: {
          title: 'Criar Sala - Impostor Game',
          description:
            'Crie uma sala privada para jogar Impostor Game com amigos em tempo real. Configure jogadores e impostores.'
        },
        backHome: '← Voltar ao início',
        header: {
          title: '🏠 Criar Nova Sala',
          subtitle: 'Configure sua sala e convide amigos'
        },
        errors: {
          createFailed: 'Erro ao criar a sala. Tente novamente.',
          nameRequiredToJoinPublic: 'Digite seu nome acima para entrar em uma sala pública.'
        },
        form: {
          nameLabel: 'Seu nome',
          namePlaceholder: 'Digite seu nome',
          passwordToggleLabel: 'Proteger com senha',
          passwordToggleHelp: 'Somente quem souber pode entrar',
          passwordLabel: 'Senha da sala',
          passwordPlaceholder: 'Digite uma senha',
          minPlayersLabel: 'Mín. jogadores',
          maxPlayersLabel: 'Máx. jogadores',
          characterLabel: 'Escolha um personagem'
        },
        activeRooms: {
          title: 'Suas salas ativas:',
          goToRoom: 'Ir para a sala',
          hint: '💡 Você pode entrar nessas salas e fechá-las para liberar espaço'
        },
        submit: {
          creating: 'Criando sala...',
          create: '🚀 Criar Sala'
        },
        footer: {
          autoCloseNote: 'A sala será fechada automaticamente após 2 horas de inatividade'
        }
      },
      join: {
        pageTitle: 'Entrar na Sala - Impostor Game',
        seo: {
          title: 'Entrar na Sala - Impostor Game',
          description: 'Entre em uma sala do Impostor Game com um código de 6 caracteres e jogue com amigos em tempo real.'
        },
        backHome: '← Voltar ao início',
        header: {
          title: '🚪 Entrar na Sala',
          subtitle: 'Digite o código para entrar no jogo'
        },
        errors: {
          roomNotFoundWithHint: 'Sala não encontrada. Verifique o código.',
          roomNotFound: 'Sala não encontrada.',
          checkRoomFailed: 'Erro ao verificar a sala.',
          wrongPassword: 'Senha incorreta.',
          joinFailed: 'Erro ao entrar na sala. Tente novamente.',
          publicRoomsFailed: 'Não foi possível carregar as salas públicas.'
        },
        form: {
          roomCodeLabel: 'Código da sala',
          roomCodeHelp: 'Código de 6 caracteres fornecido pelo anfitrião',
          nameLabel: 'Seu nome',
          namePlaceholder: 'Digite seu nome',
          characterLabel: 'Escolha um personagem',
          passwordLabel: 'Senha da sala',
          passwordPlaceholder: 'Digite a senha'
        },
        roomInfo: {
          found: '✅ Sala encontrada',
          createdBy: 'Criada por:',
          statusLabel: 'Status:',
          statusWaiting: 'Aguardando jogadores',
          statusPlaying: 'Em jogo',
          requiresPassword: '🔒 Requer senha',
          playersCount: '{{current}}/{{max}} jogadores'
        },
        submit: {
          joining: 'Entrando...',
          playingInProgress: '🎮 Jogo em andamento',
          joinRoom: '🚀 Entrar na Sala'
        },
        noCode: {
          prefix: 'Não tem um código? Pergunte ao anfitrião ou',
          createNew: 'crie uma nova'
        },
        publicRooms: {
          title: '🌐 Salas públicas ativas',
          refreshing: 'Atualizando...',
          refresh: '🔄 Atualizar',
          loading: 'Carregando salas públicas...',
          emptyTitle: 'Nenhuma sala pública disponível agora.',
          emptySubtitle: 'Seja o primeiro a criar uma!',
          hint: '💡 As salas são atualizadas automaticamente a cada 30 segundos',
          newBadge: '✨ Nova',
          hostFallback: 'Anfitrião',
          full: '🔒 Cheia',
          joinFromList: '🚀 Entrar',
          timeAgo: {
            justCreated: 'Recém-criada',
            oneMinute: 'Há 1 min',
            minutes: 'Há {{minutes}} min',
            hours: 'Há {{hours}} h'
          }
        }
      },
      contactPage: {
        pageTitle: 'Contato - Impostor Game',
        seo: {
          title: 'Contato - Impostor Game',
          description: 'Fale conosco para consultas comerciais ou envie sugestões e recomendações para melhorar o Impostor Game.'
        },
        header: {
          title: '📧 Contato',
          subtitle: 'Tem uma consulta de negócios ou uma sugestão? Adoraríamos ouvir você!'
        },
        tabs: {
          business: '💼 Negócios',
          recommendations: '💡 Sugestões'
        },
        messages: {
          success: 'Mensagem enviada com sucesso! Entraremos em contato em breve.',
          error: 'Houve um erro ao enviar sua mensagem. Tente novamente.'
        },
        business: {
          title: 'Consultas comerciais',
          description:
            'Tem uma proposta comercial, quer colaborar ou precisa de mais informações sobre nossos serviços? Preencha o formulário e entraremos em contato.',
          emailLabel: 'E-mail *',
          emailPlaceholder: 'voce@email.com',
          nameLabel: 'Nome completo *',
          namePlaceholder: 'Seu nome completo',
          subjectLabel: 'Assunto *',
          subjectPlaceholder: 'Sobre o que é?',
          descriptionLabel: 'Descrição *',
          descriptionPlaceholder: 'Descreva sua consulta, proposta ou solicitação em detalhes...',
          submitSending: '📤 Enviando...',
          submit: '📤 Enviar consulta'
        },
        recommendations: {
          title: 'Sugestões e feedback',
          infoTitle: 'Envio anônimo',
          infoText:
            'Você pode enviar pedidos, melhorias, relatos de bugs ou qualquer sugestão de forma totalmente anônima. Nenhuma informação pessoal é necessária.',
          description:
            'Seu feedback é muito valioso para nós. Ajude a melhorar o jogo compartilhando ideias, relatando problemas ou sugerindo novas funcionalidades.',
          textareaLabel: 'Sua sugestão ou feedback *',
          textareaPlaceholder:
            'Compartilhe sua sugestão, ideia de melhoria, relato de bug, pedido de funcionalidade ou qualquer comentário sobre o jogo...',
          examples:
            'Exemplos: "Seria ótimo adicionar mais personagens", "Encontrei um bug quando...", "O jogo poderia melhorar se..."',
          privacyTitle: '🔒 Privacidade',
          privacyText:
            'Esta sugestão será enviada de forma anônima. Não armazenamos nenhuma informação pessoal que possa identificar você. Apenas queremos ouvir suas ideias para melhorar o jogo.',
          submitSending: '📨 Enviando...',
          submit: '📨 Enviar sugestão'
        },
        footer: {
          preferOtherPrefix: 'Prefere outro meio de contato? Encontre-nos nas redes sociais ou envie um e-mail para'
        }
      },
      notFound: {
        seo: {
          title: 'Página não encontrada - 404 | Impostor Game',
          description: 'A página que você procura não existe. Volte ao início para jogar Impostor Game online.'
        },
        title: 'Página Não Encontrada',
        description: 'Desculpe, a página que você procura não existe ou foi movida.',
        backHome: 'Voltar ao início',
        goBlog: 'Ir ao Blog'
      },

      blogGuide: {
        brand: 'Impostor Game',
        pageTitle: 'Como jogar Impostor Game | Guia e Tutorial Completo',
        metaDescription:
          'Aprenda a jogar Impostor Game: regras, papéis, rodadas, votação e estratégias. Um guia completo passo a passo para novos jogadores.',
        nav: {
          home: 'Início',
          createRoom: 'Criar sala',
          join: 'Entrar'
        },
        title: 'Como jogar Impostor Game: guia e tutorial completo',
        intro:
          'Bem-vindo ao guia oficial do Impostor Game. Aqui você vai aprender o que é o jogo, como jogar passo a passo e todas as opções disponíveis para aproveitar ao máximo, seja com amigos, em sala de aula ou em eventos.',
        whatIs: {
          heading: 'O que é Impostor Game?',
          paragraph:
            'Impostor Game é um jogo social por rodadas inspirado em dinâmicas de dedução. Em cada partida há jogadores comuns e um ou mais impostores. Os jogadores escrevem mensagens, debatem e, ao final de cada rodada, votam para eliminar quem acreditam ser o impostor.',
          bullets: ['Ótimo para 3 a 10 jogadores', 'Partidas rápidas em tempo real', 'Perfeito para quebra-gelos, aulas e streaming']
        },
        howToPlay: {
          heading: 'Como jogar (passo a passo)',
          steps: [
            'Crie uma sala nova ou entre com um código compartilhado.',
            'Os papéis são atribuídos automaticamente: jogadores e impostores.',
            'Em cada rodada, escreva sua mensagem e leia a dos outros.',
            'Debata com o grupo e use a lógica para encontrar inconsistências.',
            'Vote para eliminar um suspeito ao final da rodada.',
            'A equipe que cumprir seu objetivo vence: jogadores se eliminarem o impostor, impostores se permanecerem ocultos.'
          ],
          ctaCreate: 'Criar sala agora',
          ctaJoin: 'Entrar em uma sala'
        },
        roles: {
          heading: 'Papéis do jogo',
          player: 'Jogador: seu objetivo é identificar comportamentos suspeitos, fazer perguntas inteligentes e votar estrategicamente.',
          impostor: 'Impostor: seu objetivo é se misturar, criar dúvidas e evitar ser descoberto até o final.'
        },
        mechanics: {
          heading: 'Mecânicas do jogo',
          bullets: [
            'Rodadas com tempo para escrever e debater.',
            'Votação anônima com contagem automática.',
            'Revelação de quem foi eliminado e avanço para a próxima rodada.',
            'Suporte a várias salas para jogar com diferentes grupos.'
          ]
        },
        options: {
          heading: 'Opções e configurações',
          bullets: [
            'Número recomendado de jogadores: 3 a 10.',
            'Papéis atribuídos automaticamente para equilibrar a partida.',
            'Chat por rodadas com ritmo controlado.',
            'Votação ao final de cada rodada com desempates automáticos.'
          ]
        },
        tips: {
          heading: 'Dicas para vencer',
          bullets: ['Observe a linguagem e as contradições.', 'Faça perguntas abertas para forçar explicações.', 'Como impostor, mantenha mensagens convincentes e evite exageros.']
        },
        faq: {
          heading: 'Perguntas frequentes',
          items: [
            { q: 'Preciso de uma conta?', a: 'Não, apenas um nome para entrar em uma sala.' },
            { q: 'Funciona no celular?', a: 'Sim, é otimizado para dispositivos móveis.' },
            { q: 'Quanto dura uma partida?', a: 'Cerca de 5 a 15 minutos, dependendo do número de jogadores.' }
          ]
        },
        ready: {
          title: 'Pronto para jogar?',
          subtitle: 'Crie uma sala e convide seus amigos em segundos.',
          create: 'Criar sala',
          join: 'Entrar na sala',
          backHome: 'Voltar ao início'
        },
        footer: 'Impostor Game • Tutorial e guia do jogo'
      },

      blogOnline: {
        brand: 'Impostor Game',
        pageTitle: 'Modo Multijogador Online - Impostor Game',
        metaDescription:
          'Jogue Impostor Game no modo multijogador online com amigos de qualquer lugar. Conexão em tempo real, votação instantânea e diversão garantida.',
        nav: {
          home: 'Início',
          blog: 'Blog',
          createRoom: 'Criar sala'
        },
        title: '🌐 Modo Multijogador Online',
        subtitle: 'Jogue com amigos de qualquer lugar em tempo real',
        whatIs: {
          heading: 'O que é o modo online?',
          paragraph:
            'O modo multijogador online do Impostor Game permite jogar com amigos não importa onde estejam. Basta compartilhar um código de sala e todos podem entrar pelo celular ou computador.',
          benefitTitle: '✨ Principal benefício:',
          benefitText:
            'Conecte-se com amigos, familiares ou colegas de trabalho de qualquer lugar. Ideal para encontros virtuais, aulas online ou simplesmente se divertir à distância.'
        },
        features: {
          heading: 'Principais recursos',
          items: [
            'Conexão em tempo real: Todos veem atualizações instantaneamente',
            'Multissala: Várias partidas podem acontecer ao mesmo tempo',
            'Sem instalação: Só é necessário um navegador',
            'Compatível com celular: Jogue em qualquer dispositivo',
            'Chat por rodadas: Debate estratégico em cada fase',
            'Votação anônima: Ninguém sabe quem votou em quem até o final'
          ]
        },
        steps: {
          heading: 'Como jogar (passo a passo)',
          items: [
            'Crie ou entre em uma sala: o anfitrião cria a sala e compartilha o código',
            'Aguarde os jogadores: os demais entram usando o código de 6 caracteres',
            'Distribuição de papéis: o sistema atribui automaticamente (jogadores e impostores)',
            'Rodadas do jogo: a cada rodada, escreva mensagens e leia as dos outros',
            'Debate: use a lógica para identificar comportamentos suspeitos',
            'Votação: ao final de cada rodada, vote para eliminar um suspeito',
            'Vitória: jogadores vencem ao eliminar o impostor; impostores vencem ao permanecer ocultos'
          ],
          ctaCreate: 'Criar sala agora',
          ctaJoin: 'Entrar em uma sala'
        },
        useCases: {
          heading: 'Casos de uso ideais',
          items: [
            { title: 'Reuniões virtuais', description: 'Perfeito como quebra-gelo em chamadas de trabalho ou eventos online' },
            { title: 'Educação online', description: 'Uma ferramenta dinâmica para aulas virtuais e pensamento crítico' },
            { title: 'Jogos com amigos', description: 'Organize partidas com amigos em diferentes cidades ou países' },
            { title: 'Streaming', description: 'Ótimo para streamers que querem interagir com a comunidade' }
          ]
        },
        tips: {
          heading: 'Dicas para o modo online',
          items: [
            'Use uma chamada de voz/vídeo paralela: combine com Discord, Zoom ou Meet para maior imersão',
            'Defina regras claras: combine tempo de debate e diretrizes antes de começar',
            'Mantenha grupos equilibrados: 4–8 jogadores é um ótimo número',
            'Conexão estável: garanta uma internet confiável',
            'Seja respeitoso: mantenha o ambiente divertido e amigável'
          ]
        },
        faq: {
          heading: 'Perguntas frequentes',
          items: [
            { q: 'Preciso de uma conta para jogar?', a: 'Não, apenas um nome para entrar na sala.' },
            { q: 'Quantos jogadores podem entrar?', a: 'Recomendamos 3 a 10 jogadores para a melhor experiência.' },
            { q: 'Funciona no celular?', a: 'Sim, é totalmente otimizado para dispositivos móveis.' },
            { q: 'Quanto dura uma partida?', a: 'Cerca de 5 a 15 minutos, dependendo dos jogadores e rodadas.' },
            { q: 'Posso jogar de forma privada?', a: 'Sim — compartilhe o código apenas com quem deseja convidar.' }
          ]
        },
        ready: {
          title: 'Pronto para jogar online?',
          subtitle: 'Crie uma sala e convide seus amigos em segundos. A diversão está a um clique!',
          create: 'Criar sala',
          join: 'Entrar na sala',
          backHome: 'Voltar ao início'
        },
        footer: 'Impostor Game • Modo Multijogador Online'
      },

      blogTable: {
        brand: 'Impostor Game',
        pageTitle: 'Modo Mesa - Impostor Game',
        metaDescription:
          'Jogue Impostor Game no modo mesa para encontros presenciais. Um dispositivo, diversão cara a cara, sem necessidade de múltiplas conexões.',
        nav: {
          home: 'Início',
          blog: 'Blog',
          playTable: 'Jogar no modo mesa'
        },
        title: '🎲 Modo Mesa',
        subtitle: 'Um dispositivo, máxima interação presencial',
        whatIs: {
          heading: 'O que é o modo mesa?',
          paragraph:
            'O modo mesa do Impostor Game é pensado para encontros presenciais usando um único dispositivo. Perfeito para festas, reuniões em família, salas de aula ou qualquer evento em que todos estejam no mesmo lugar.',
          benefitTitle: '✨ Principal benefício:',
          benefitText:
            'Aproveite a interação cara a cara sem distrações digitais. Você só precisa de um dispositivo (tablet, notebook ou tela) e todos participam juntos. Ótimo para fortalecer vínculos sociais e criar momentos memoráveis.'
        },
        features: {
          heading: 'Principais recursos',
          items: [
            'Um dispositivo: os jogadores não precisam de seus próprios celulares',
            'Revelação individual do papel: cada jogador vê seu papel em privado',
            'Discussão presencial: debate cara a cara para maior imersão',
            'Votação presencial: vote com as mãos ou usando o dispositivo compartilhado',
            'Sem internet necessária: após carregar, pode funcionar offline',
            'Ideal para grupos locais: perfeito para eventos presenciais'
          ]
        },
        steps: {
          heading: 'Como jogar (passo a passo)',
          items: [
            'Configure o jogo: digite os nomes de todos em um dispositivo',
            'Distribua os papéis: o sistema atribui papéis secretos para cada jogador',
            'Revele os papéis: cada jogador vê seu papel em privado',
            'Debate presencial: discutam quem pode ser o impostor',
            'Votação: cada jogador vota apontando ou usando o dispositivo compartilhado',
            'Revelação: o jogador eliminado e seu papel são revelados',
            'Vitória: o jogo continua até o impostor ser encontrado ou os impostores vencerem'
          ],
          ctaPlay: 'Jogar no modo mesa'
        },
        useCases: {
          heading: 'Casos de uso ideais',
          items: [
            { title: 'Festas e encontros', description: 'Perfeito para animar aniversários, reuniões de família ou eventos sociais' },
            { title: 'Sala de aula e educação', description: 'Ferramenta educativa para comunicação e pensamento crítico' },
            { title: 'Team building', description: 'Atividade de integração para equipes e dinâmica de trabalho' },
            { title: 'Cafés e espaços sociais', description: 'Ótimo para grupos de amigos se encontrando pessoalmente' }
          ]
        },
        advantages: {
          heading: 'Vantagens do modo mesa',
          items: [
            { title: 'Linguagem corporal', description: 'Observe gestos, olhares e reações em tempo real' },
            { title: 'Custo-benefício', description: 'Sem necessidade de vários dispositivos ou conexões' },
            { title: 'Mais imersivo', description: 'A interação cara a cara torna tudo mais emocionante' },
            { title: 'Configuração rápida', description: 'Comece a jogar em menos de um minuto' }
          ]
        },
        tips: {
          heading: 'Dicas para o modo mesa',
          items: [
            'Use uma tela grande: melhor experiência em tablet, notebook ou TV',
            'Mantenha os papéis em segredo: garanta que ninguém espreite',
            'Defina limites de tempo: decida quanto dura cada rodada de debate',
            'Crie a disposição ideal: sente-se em círculo para todos se verem',
            'Respeito e diversão: mantenha um clima leve e amigável'
          ]
        },
        comparison: {
          heading: 'Mesa vs Online: qual escolher?',
          headers: {
            aspect: 'Aspecto',
            table: 'Modo Mesa',
            online: 'Modo Online'
          },
          rows: [
            { aspect: 'Dispositivos necessários', table: '1 dispositivo total', online: '1 por jogador' },
            { aspect: 'Local', table: 'Mesmo lugar', online: 'Em qualquer lugar' },
            { aspect: 'Interação', table: 'Cara a cara', online: 'Virtual/texto' },
            { aspect: 'Ideal para', table: 'Festas, aulas, encontros presenciais', online: 'Amigos à distância, trabalho remoto' }
          ]
        },
        faq: {
          heading: 'Perguntas frequentes',
          items: [
            { q: 'Preciso de internet para o modo mesa?', a: 'Apenas para carregar a página inicialmente. Depois de carregada, pode funcionar offline.' },
            { q: 'Quantos jogadores podem participar?', a: 'Recomendamos 3 a 10 jogadores para a melhor experiência.' },
            { q: 'Qual dispositivo é melhor?', a: 'Um tablet ou notebook é ideal, mas celulares também funcionam.' },
            { q: 'Como os papéis ficam em segredo?', a: 'O jogo revela os papéis individualmente e os outros jogadores não devem olhar.' },
            { q: 'Posso usar o modo mesa em sala de aula?', a: 'Com certeza! É uma ótima ferramenta educativa para habilidades sociais e cognitivas.' }
          ]
        },
        ready: {
          title: 'Pronto para jogar no modo mesa?',
          subtitle: 'Reúna seu grupo, prepare o dispositivo e comece a diversão cara a cara!',
          play: 'Jogar no modo mesa',
          backHome: 'Voltar ao início'
        },
        footer: 'Impostor Game • Modo Mesa'
      },

      gamesShowcase: {
        loading: 'Carregando jogos...',
        empty: 'Nenhum jogo disponível',
        title: 'Descubra Nossos Jogos',
        subtitle: 'Explore nossa coleção e escolha sua próxima aventura'
      },

      categorySelector: {
        selectPlaceholder: 'Selecione uma categoria',
        searchPlaceholder: '🔍 Buscar categoria...',
        noResults: 'Nenhuma categoria encontrada',
        charactersCount: '{{count}} personagens',
        showMore: 'Mostrar mais (restam {{count}})',
        showing: 'Mostrando {{visible}} de {{total}} categorias',
        filteredSuffix: '(filtrado de {{all}} total)'
      },

      gameRoom: {
        seo: {
          title: 'Sala {{roomCode}} - Impostor Game',
          description:
            'Sala do Impostor Game em tempo real. Participe da dedução social e descubra quem é o impostor.'
        },
        header: {
          title: 'Sala {{roomCode}}',
          playersConnected: '{{count}} jogadores conectados',
          yourCharacterAlt: 'Seu personagem',
          roomArtAlt: 'Arte da sala',
          copyLink: '📋 Copiar link',
          closeRoom: '🔒 Fechar sala',
          leave: '🚪 Sair'
        },
        websocket: {
          connected: 'Conectado em tempo real',
          disconnected: 'Desconectado - Tentando novamente...'
        },
        systemMessage: {
          default: 'Notificação do sistema'
        },
        reconnecting: {
          title: '🔄 Reconectando',
          restoring: 'Restaurando sua sessão...',
          retrying: 'Tentando reconectar ({{current}}/{{max}})...',
          updatingSession: 'Atualizando sessão...',
          help: 'Se isso demorar muito, tente recarregar a página',
          brand: '🎭 Impostor Game',
          overlayTitle: 'Reconectando ao jogo',
          attempt: 'Tentativa {{current}} de {{max}}',
          server: 'Restabelecendo conexão com o servidor...',
          overlayFooter: 'Impostor Game - Multijogador'
        },
        confirm: {
          beforeUnload: 'Tem certeza de que deseja sair? O jogo está em andamento.',
          leaveTitle: 'Sair do jogo?',
          leaveMessage: 'Tem certeza de que deseja sair? O jogo está em andamento e você perderá seu progresso.',
          closeTitle: 'Fechar sala?',
          closeMessage:
            'Tem certeza de que deseja fechar a sala? Todos os jogadores serão desconectados e a sala ficará inativa. Isso liberará espaço para novas salas.'
        },
        errors: {
          loadRoomInfo: 'Erro ao carregar informações da sala',
          joinFailed: 'Não foi possível entrar na sala',
          reconnectFailed: 'Não foi possível reconectar',
          connectionError: '⚠️ Erro de conexão: {{message}}. Tente recarregar a página manualmente.',
          startGame: 'Erro ao iniciar o jogo',
          roomNotWaiting:
            'A sala não está em estado de espera. Parece que um jogo está em andamento. Se você é o anfitrião, pode encerrá-lo para iniciar outro.',
          endGameFailed: 'Não foi possível encerrar o jogo',
          closeRoomFailed: 'Erro ao fechar a sala',
          reopenRoomFailed: 'Erro ao reabrir a sala',
          voteFailed: 'Erro ao votar',
          copyTryAgain: 'Não foi possível copiar. Tente novamente.',
          copyClipboard: 'Erro ao copiar para a área de transferência',
          copyPrompt: 'Copie este link:',
          kickedRoomClosed: 'O anfitrião fechou a sala',
          kickedByCreator: 'Você foi expulso da sala pelo anfitrião',
          roomClosed: 'A sala foi fechada pelo anfitrião'
        },
        turn: {
          confirmFailed: 'Não foi possível confirmar seu turno'
        },
        players: {
          kickFailed: 'Erro ao expulsar jogador'
        },
        waiting: {
          inProgressTitle: '🎮 Jogo em andamento',
          statusPrefix: 'Status da sala: {{status}}.',
          creatorHint: 'Você pode encerrá-lo se quiser iniciar outro.',
          nonCreatorHint: 'Aguarde o anfitrião encerrar o jogo.',
          endGame: '🛑 Encerrar jogo',
          waitingCreatorActions: 'Aguardando o anfitrião…',
          title: '🎭 Aguardando jogadores',
          minPlayers: 'Mínimo de 3 jogadores para começar',
          gameCategoryTitle: '📚 Categoria do jogo',
          noCategory: 'Sem categoria',
          onlyCreatorCanChange: 'Apenas o anfitrião pode mudar a categoria',
          changeCategoryLabel: 'Alterar categoria',
          startGame: '🚀 Iniciar jogo',
          waitingAllReady: 'Aguardando todos os jogadores ficarem prontos...',
          waitingCreatorStart: 'Aguardando o anfitrião iniciar o jogo...'
        },
        assigningRoles: {
          title: '🎲 Atribuindo papéis...'
        },
        roleCard: {
          tapToHide: 'Toque para ocultar seu papel',
          tapToReveal: 'Toque para revelar seu papel',
          hideRoleInfo: 'Ocultar informações do papel',
          showRoleInfo: 'Mostrar informações do papel',
          impostor: {
            title: '😈 VOCÊ É O IMPOSTOR!',
            remember: '⚠️ LEMBRE-SE: A categoria é',
            categoryHint: 'Você deve fingir que pertence a esta categoria',
            instructions:
              'Escreva como se fosse parte da categoria. Confunda os outros fingindo que se encaixa perfeitamente.',
            tapToHide: 'Toque para ocultar esta informação'
          },
          player: {
            title: '👤 Você é um jogador comum',
            categoryLead: '📝 Seu personagem pertence a:',
            remember: 'Lembre-se disso ao escrever e votar',
            goal: 'Seu objetivo é encontrar o impostor',
            tapToHide: 'Toque para ocultar esta informação'
          },
          generic: {
            title: '🎭 Seu papel',
            info: 'Informações do papel disponíveis',
            tapToHide: 'Toque para ocultar esta informação'
          },
          hidden: {
            title: '🤐 Informação oculta',
            subtitle: 'Toque para revelar seu papel',
            hint: 'Toque em qualquer lugar desta área'
          }
        },
        writing: {
          round: '📝 Rodada {{round}}',
          turnOf: 'Turno: {{name}}',
          yourTurnTitle: '✍️ É A SUA VEZ!',
          yourTurnSubtitle: 'Escreva sua mensagem com base no seu papel',
          yourTurnHint: '📋 Você pode ver seu papel completo acima',
          placeholder: '✍️ Escreva sua mensagem aqui...',
          confirmProcessing: '⏳ Processando turno...',
          confirmSend: '✅ Confirmar e enviar mensagem',
          otherPlayer: 'Outro jogador',
          otherIsWriting: '{{name}} está escrevendo',
          waitTurn: 'Aguarde sua vez para participar'
        },
        reading: {
          title: '👀 Leia as mensagens de todos',
          votingIn: 'Votação em:',
          preparing: 'Preparando...',
          preparingVotingTitle: '⏳ Preparando votação...',
          preparingVotingSubtitle: 'Organizando informações',
          didNotWrite: 'Não escreveu nada nesta rodada'
        },
        voting: {
          eliminatedTitle: '🗳️ Votação em andamento',
          eliminatedBadge: 'VOCÊ FOI ELIMINADO',
          eliminatedCantVoteTitle: '❌ Você não pode votar nesta rodada',
          eliminatedCantVoteBody: 'Você foi eliminado. Os jogadores restantes estão votando.',
          activeTitle: '🗳️ Vote no impostor!',
          activeBadge: 'VOTANDO',
          processing: 'Processando...',
          processingTitle: '⏳ Processando resultados...',
          processingSubtitle: 'Contando votos',
          voteSent: '✅ Voto enviado',
          confirmVote: '🗳️ Confirmar voto'
        },
        results: {
          title: '📊 Resultados da votação',
          preparing: 'Preparando...',
          preparingNextTitle: '⏳ Preparando próxima fase...',
          preparingNextSubtitle: 'Iniciando próxima rodada',
          votes: '{{count}} votos'
        },
        finished: {
          title: '🎉 Fim de jogo!',
          roundsCompleted: 'Rodadas concluídas: {{count}}',
          winnersPlayers: '✅ Jogadores vencem',
          winnersImpostors: '⚠️ Impostores vencem',
          wordWas: '🎯 A palavra era:',
          impostorWas: '🎭 O impostor foi:',
          impostorsWere: '🎭 Os impostores foram:',
          backHome: '🏠 Voltar ao início',
          backWaiting: '🔁 Voltar à sala de espera'
        },
        playersPanel: {
          title: '👥 Jogadores',
          you: 'Você',
          kick: 'Expulsar {{name}}'
        },
        connectionStatus: {
          inGame: 'Em jogo',
          connecting: 'Conectando...',
          ready: 'Pronto',
          disconnected: 'Desconectado'
        },
        reactions: {
          send: 'Enviar reação'
        },
        clipboard: {
          copied: 'Link copiado para a área de transferência!'
        },
        toast: {
          roomClosedSuccess: 'Sala fechada com sucesso'
        }
      },

      tableSetup: {
        pageTitle: 'Modo Mesa - Impostor Game',
        seo: {
          title: 'Modo Mesa - Impostor Game',
          description:
            'Configure sua partida presencial do Impostor Game no modo mesa. Escolha o número de jogadores, impostores e a palavra secreta.'
        },
        header: {
          title: '🎲 Modo Mesa',
          subtitle: 'Configure seu jogo local',
          infoTitle: 'Modo Mesa',
          infoContent:
            'O modo mesa é projetado para jogar presencialmente com um único dispositivo. Cada jogador verá seu papel em privado e depois vocês podem jogar sem precisar de conexão com a internet.'
        },
        playerCount: {
          label: 'Número de jogadores',
          infoTitle: 'Número de jogadores',
          infoContent: 'Selecione quantas pessoas vão jogar. Você pode escolher entre 3 e 10 jogadores.'
        },
        playerNames: {
          title: 'Nomes dos jogadores',
          label: 'Jogador {{index}}',
          placeholder: 'Jogador {{index}}',
          defaultName: 'Jogador {{index}}'
        },
        wordMode: {
          label: 'Modo de palavra',
          infoTitle: 'Modo de palavra',
          infoContent:
            'Escolha como a palavra secreta será selecionada. Aleatória: escolhida automaticamente. Personalizada: você escolhe a palavra. Categoria: escolhida de uma categoria específica.',
          random: '🎲 Aleatória',
          custom: '✍️ Personalizada',
          category: '📂 Categoria',
          customPlaceholder: 'Digite a palavra secreta...'
        },
        impostors: {
          label: 'Número de impostores',
          infoTitle: 'Número de impostores',
          infoContent:
            'Normal: segue regras clássicas (menos da metade são impostores). Livre: escolha qualquer quantidade, até 0 ou todos os jogadores.',
          normal: '⚖️ Normal',
          free: '🆓 Livre',
          countLabel: 'Número de impostores: {{count}}',
          maxLabel: '(máx: {{max}})'
        },
        actions: {
          starting: 'Iniciando...',
          start: '🚀 Iniciar jogo',
          backHome: '← Voltar ao início'
        },
        errors: {
          customWordRequired: 'Você precisa informar uma palavra no modo personalizado',
          categoryRequired: 'Você precisa selecionar uma categoria',
          startFailed: 'Ocorreu um erro ao iniciar o jogo. Tente novamente.'
        }
      },

      tableGame: {
        pageTitle: 'Jogando - Modo Mesa',
        seo: {
          title: 'Jogando - Modo Mesa',
          description: 'Partida em andamento no modo mesa. Revele seu papel e participe da dedução social.'
        },
        progress: {
          playerOf: 'Jogador {{current}} de {{total}}'
        },
        turnOf: 'Vez de {{name}}',
        tapCardHint: 'Toque na carta para ver seu papel',
        cardBack: {
          tapToReveal: 'Toque para revelar'
        },
        cardFront: {
          impostor: 'IMPOSTOR',
          player: 'JOGADOR',
          yourWord: 'Sua palavra é:',
          impostorHint: 'Descubra a palavra sem ser descoberto!'
        },
        next: {
          finish: '✅ Finalizar distribuição',
          passTo: '➡️ Passar para {{name}}',
          revealFirst: 'Revele sua carta primeiro para ver seu papel'
        },
        instructions: {
          privacy: '💡 Certifique-se de que ninguém veja a tela antes de revelar seu papel'
        },
        modalNext: {
          title: 'Vez de {{name}}',
          body: 'Passe o dispositivo para {{name}} e pressione continuar quando estiver pronto.',
          continue: 'Continuar'
        },
        modalComplete: {
          title: 'Todos prontos!',
          body: 'Todos os jogadores têm seus papéis. Que o jogo comece!',
          newGame: '🔄 Novo jogo',
          goHome: '🏠 Ir ao início'
        }
      },

      infoModal: {
        closeAria: 'Fechar',
        howToPlay: 'Como jogar',
        moreInfo: '📖 Mais informações',
        close: 'Fechar'
      }
    }
  }
} as const;

void i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: typeof window !== 'undefined' ? getLocaleFromPathname(window.location.pathname) : 'es',
    fallbackLng: 'es',
    supportedLngs: ['es', 'en', 'pt'],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    interpolation: { escapeValue: false }
  });

export default i18n;
